import React, { useEffect, useRef, useState } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Check, ChevronLeft, Copy, Crop, Download, Info, Instagram, Lock, Maximize, Heart, Share2, X, ZoomIn, ZoomOut, Sparkles, Pencil, Video, MoreHorizontal, Loader2 } from 'lucide-react';
import { GENERATE_AUTH_TOKEN } from '../config';
import { supabase } from '../lib/supabase';
import SubscriptionLimitModal from './SubscriptionLimitModal';
import CarruselConfirmModal from './CarruselConfirmModal';
import LoadingAnimation from './LoadingAnimation';
import { useCropState, ImageCropCanvas, ImageCropControls } from './ImageCropModal';
import { trackInteraction } from '../utils/trackInteraction';
import { mergeLastCrearAnuncioState } from '../utils/crearAnuncioStorage';
import { getCachedSignedUrl, setCachedSignedUrl } from '../utils/imageCache';
import { useSidebar } from '../context/SidebarContext';

// AI Information content component for the tooltip (copied from full modal)
const AIInfoContent = ({ isFashion = true }: { isFashion?: boolean }) => (
  <div className="flex flex-col space-y-3 sm:space-y-4">
    <div className="text-[13px] sm:text-[15px]">
      <p className="text-[13px] sm:text-[15px]">
        La inteligencia artificial es la tecnología de más rápida evolución en el mundo, por lo que mejora día a día.
        Sin embargo, como cualquier tecnología nueva, a veces no es perfecta. Queremos contarte de forma transparente sobre los detalles que estamos perfeccionando:
      </p>
      <ul className="list-disc pl-4 sm:pl-5 mt-1 sm:mt-2 space-y-0.5 sm:space-y-1">
        <li className="text-[13px] sm:text-[15px]">Rostros: Ocasionalmente, pueden aparecer pequeñas inconsistencias en los rasgos faciales.</li>
        <li className="text-[13px] sm:text-[15px]">Texto: El texto de tamaño reducido puede generarse de forma ilegible.</li>
        {isFashion ? (
          <>
            <li className="text-[13px] sm:text-[15px]">Fidelidad de la prenda: Pueden existir ligeras diferencias entre la prenda original y su representación en la imagen.</li>
            <li className="text-[13px] sm:text-[15px]">Restricciones de contenido: Para prevenir el uso inadecuado de la plataforma, hemos limitado la generación de imágenes con prendas de poca cobertura (como lencería o ropa interior).</li>
          </>
        ) : (
          <>
            <li className="text-[13px] sm:text-[15px]">Fidelidad del producto: Pueden existir ligeras diferencias entre el producto original y su representación en la imagen.</li>
            <li className="text-[13px] sm:text-[15px]">Restricciones de contenido: Para prevenir el uso inadecuado de la plataforma, hemos limitado la generación de imágenes con ciertos tipos de productos y configuraciones, como armas, objetos para adultos, y otros contenidos sensibles.</li>
          </>
        )}
      </ul>
      <p className="mt-1 sm:mt-2 text-[13px] sm:text-[15px]">
        Nuestro equipo, junto con expertos de toda la industria, trabaja sin descanso para que la IA sea cada vez más precisa y potente.
        Gracias por ser parte de este viaje y por confiar en nosotros.
      </p>
    </div>
    <div className="flex justify-center mt-3 sm:mt-4">
      <img 
        src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Firma.png" 
        alt="Firma del equipo" 
        className="h-12 sm:h-16 object-contain" 
      />
    </div>
  </div>
);

// Helper to format date in a smart way (matches GeneratedImagesHistory)
const formatSmartDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const dayBeforeYesterday = new Date(today);
  dayBeforeYesterday.setDate(dayBeforeYesterday.getDate() - 2);
  const oneWeekAgo = new Date(today);
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const timeStr = `${hours}:${minutes} hs`;
  if (dateOnly.getTime() === today.getTime()) return `Hoy ${timeStr}`;
  if (dateOnly.getTime() === yesterday.getTime()) return `Ayer ${timeStr}`;
  if (dateOnly.getTime() === dayBeforeYesterday.getTime()) return `Anteayer ${timeStr}`;
  if (dateOnly >= oneWeekAgo) {
    const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    return `${dayNames[date.getDay()]} ${timeStr}`;
  }
  const monthNames = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  return `${date.getDate()} de ${monthNames[date.getMonth()]}`;
};

interface GeneratedImage {
  id: string;
  fileName: string;
  signedUrl: string;
  optSignedUrl?: string;
  favorito?: boolean;
  isVideo?: boolean;
  created_at?: string;
  variante_nombre?: string;
}

interface GeneratedImagesInlineProps {
  imageIds: (number | string)[];
  companyId: string;
  onClose?: () => void;
  onReturnToConfig?: () => void;
  onGenerateMore?: () => Promise<{ imageIds?: (number | string)[]; companyId?: string } | void> | void;
  onReady?: () => void;
  preloadedImages?: GeneratedImage[] | null;
  isOnboarding?: boolean;
}

const GeneratedImagesInline: React.FC<GeneratedImagesInlineProps> = ({
  imageIds,
  companyId,
  onClose,
  onReturnToConfig,
  onGenerateMore,
  onReady,
  preloadedImages,
  isOnboarding = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarWidth, setMobileTitleOverride, setMobileHideBack } = useSidebar();
  
  // Check if we're on the dedicated page for full-width layout
  const isStandalonePage = location.pathname === '/redes-sociales/imagenes-generadas';

  useEffect(() => {
    try {
      console.log('[GeneratedImagesInline] mounted', {
        pathname: window.location.pathname,
        search: window.location.search,
      });
    } catch {}
    return () => {
      try { console.log('[GeneratedImagesInline] unmounted'); } catch {}
    };
  }, []);

  // Initialize states based on whether we have preloaded images
  const hasPreloadedOnMount = !!(preloadedImages && preloadedImages.length > 0);

  // Mobile view mode: 'grid' shows thumbnail grid + history, 'detail' shows single image with swipe
  // Auto-expand to detail when arriving with a video (e.g. from History "Animar")
  const hasPreloadedVideo = hasPreloadedOnMount && preloadedImages!.some(img => img.isVideo);
  const [mobileView, setMobileView] = useState<'grid' | 'detail'>(hasPreloadedVideo ? 'detail' : 'grid');

  // Set mobile header title via context — dynamic based on view
  useEffect(() => {
    if (mobileView === 'grid') {
      setMobileTitleOverride('Imágenes Generadas');
    } else {
      setMobileTitleOverride('Imagen');
    }
    return () => setMobileTitleOverride(null);
  }, [setMobileTitleOverride, mobileView]);

  // Hide mobile back button during onboarding (only in grid view; detail view needs back to return to grid)
  useEffect(() => {
    if (isOnboarding) {
      setMobileHideBack(mobileView === 'grid');
      return () => setMobileHideBack(false);
    }
  }, [isOnboarding, mobileView, setMobileHideBack]);
  
  // State variables (kept in parity with full modal)
  const [loading, setLoading] = useState<boolean>(!hasPreloadedOnMount);
  const [images, setImages] = useState<GeneratedImage[]>(hasPreloadedOnMount ? preloadedImages : []);
  const [selectedImageIds, setSelectedImageIds] = useState<string[]>(hasPreloadedOnMount && preloadedImages.length > 0 ? [preloadedImages[0].id] : []);
  const [fullScreenImage, setFullScreenImage] = useState<GeneratedImage | null>(null);
  const [currentViewImage, setCurrentViewImage] = useState<GeneratedImage | null>(hasPreloadedOnMount && preloadedImages.length > 0 ? preloadedImages[0] : null);
  const [zoomLevel, setZoomLevel] = useState<number>(100);
  const [error, setError] = useState<string | null>(null);
  const [showInstagramPreview, setShowInstagramPreview] = useState<boolean>(false);
  const [instagramUsername, setInstagramUsername] = useState('instagram_username');
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [isPostingToInstagram, setIsPostingToInstagram] = useState(false);
  const [instagramPostSuccess, setInstagramPostSuccess] = useState(false);
  const [postDescription, setPostDescription] = useState('Moda fashion . veni a visitarnos');
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(postDescription);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const [isInstagramConnected, setIsInstagramConnected] = useState<boolean>(false);
  const [showConnectPrompt, setShowConnectPrompt] = useState<boolean>(false);
  const [isGeneratingMore, setIsGeneratingMore] = useState<boolean>(false);
  const [isGeneratingNewAngles, setIsGeneratingNewAngles] = useState<boolean>(false);
  const [newAnglesProgressComplete, setNewAnglesProgressComplete] = useState<boolean>(false);
  const [generatingPlaceholderCount, setGeneratingPlaceholderCount] = useState<number>(0);
  const [isFavoriting, setIsFavoriting] = useState<boolean>(false);
  const [showFavoriteMessage, setShowFavoriteMessage] = useState<boolean>(false);
  const [showMoreOptions, setShowMoreOptions] = useState<boolean>(false);
  const [showCropModal, setShowCropModal] = useState<boolean>(false);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 768px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  // Always use the full-resolution URL for cropping (not the opt version)
  const [cropFullUrl, setCropFullUrl] = useState('');
  useEffect(() => {
    if (!showCropModal || !currentViewImage) { setCropFullUrl(''); return; }
    // If signedUrl !== optSignedUrl, it's already the full version
    if (!currentViewImage.optSignedUrl || currentViewImage.signedUrl !== currentViewImage.optSignedUrl) {
      setCropFullUrl(currentViewImage.signedUrl);
      return;
    }
    // Otherwise, fetch the full version
    const fullPath = `${companyId}/full/${currentViewImage.fileName}`;
    const cached = getCachedSignedUrl(fullPath);
    if (cached) {
      setCropFullUrl(cached);
    } else {
      supabase.storage.from('generadas').createSignedUrl(fullPath, 3600).then(({ data }) => {
        if (data?.signedUrl) {
          setCachedSignedUrl(fullPath, data.signedUrl);
          setCropFullUrl(data.signedUrl);
        }
      });
    }
  }, [showCropModal, currentViewImage, companyId]);
  const cropState = useCropState(
    cropFullUrl,
    currentViewImage?.fileName || '',
    () => setShowCropModal(false)
  );
  const [showDescriptionPanel, setShowDescriptionPanel] = useState<boolean>(false);
  const [generatedDescription, setGeneratedDescription] = useState<string>('');
  const [copiedDescription, setCopiedDescription] = useState<boolean>(false);
  const [descriptionProgressActive, setDescriptionProgressActive] = useState<boolean>(false);
  const [showSubscriptionLimitModal, setShowSubscriptionLimitModal] = useState<boolean>(false);
  const [subscriptionLimitType, setSubscriptionLimitType] = useState<'free-trial-ended' | 'plan-limit-reached'>('free-trial-ended');
  const [insufficientModal, setInsufficientModal] = useState<{ action: string; required: number; remaining: number; renewalDate: Date | null } | null>(null);
  const [showEditPlanRestriction, setShowEditPlanRestriction] = useState<boolean>(false);
  const [showVideoPlanRestriction, setShowVideoPlanRestriction] = useState<boolean>(false);
  const [isFashionRubro, setIsFashionRubro] = useState<boolean>(true);
  const [showCarruselConfirm, setShowCarruselConfirm] = useState<boolean>(false);

  // Video generation menu states
  const [showVideoMenu, setShowVideoMenu] = useState<boolean>(false);
  const [videoMenuAnimated, setVideoMenuAnimated] = useState<boolean>(false);
  const [videoMenuDragY, setVideoMenuDragY] = useState<number>(0);
  const videoDragStartY = useRef<number | null>(null);
  const [videoMode, setVideoMode] = useState<'automatico' | 'describir'>('automatico');
  const [videoStep, setVideoStep] = useState<'select' | 'prompt'>('select');
  const [videoPrompt, setVideoPrompt] = useState<string>('');
  const [videoDuration, setVideoDuration] = useState<'4' | '6' | '8'>('6');
  const [isGeneratingVideo, setIsGeneratingVideo] = useState<boolean>(false);
  const [videoProgressComplete, setVideoProgressComplete] = useState<boolean>(false);

  const [historyImages, setHistoryImages] = useState<GeneratedImage[]>([]);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
  const [hasMoreHistory, setHasMoreHistory] = useState<boolean>(true);
  const [loadingMoreHistory, setLoadingMoreHistory] = useState<boolean>(false);
  // Mobile scroll container ref + saved scroll position
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const savedGridScrollTop = useRef<number>(0);
  // Hero animation state (Apple Photos style: thumbnail ↔ fullscreen)
  const heroNaturalSize = useRef<{ w: number; h: number } | null>(null);
  const [heroRect, setHeroRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [heroAnimating, setHeroAnimating] = useState<boolean>(false);
  const [heroPhase, setHeroPhase] = useState<'start' | 'end'>('start');
  const [heroDirection, setHeroDirection] = useState<'forward' | 'reverse'>('forward');
  const [heroTargetRect, setHeroTargetRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  // Scroll-based swipe for detail view
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isScrollProgrammatic = useRef<boolean>(false);
  // All images combined for navigation (current batch + history)
  const allGridImages = React.useMemo(() => {
    const currentIds = new Set(images.map(img => img.id));
    const uniqueHistory = historyImages.filter(img => !currentIds.has(img.id));
    return [...images, ...uniqueHistory];
  }, [images, historyImages]);

  // Animation states - always start at initial, trigger animation after mount if preloaded
  const [animationPhase, setAnimationPhase] = useState<'initial' | 'phase1' | 'phase2' | 'complete'>('initial');
  const [mainImageLoaded, setMainImageLoaded] = useState<boolean>(hasPreloadedOnMount);
  const [loadedThumbnails, setLoadedThumbnails] = useState<Set<number | string>>(hasPreloadedOnMount ? new Set(preloadedImages.map(img => img.id)) : new Set());
  const allThumbnailsLoaded = images.length > 0 && loadedThumbnails.size >= images.length;

  // Signal parent when animation has started (images are becoming visible)
  const hasSignaledReadyRef = useRef<boolean>(false);
  
  // Track if we've triggered the initial preload animation (to avoid re-triggering on "generate more")
  const hasTriggeredPreloadAnimationRef = useRef<boolean>(false);
  
  // Trigger animation after mount if preloaded (only once)
  useEffect(() => {
    if (hasPreloadedOnMount && animationPhase === 'initial' && !hasTriggeredPreloadAnimationRef.current) {
      hasTriggeredPreloadAnimationRef.current = true;
      // Small delay to ensure the initial state is rendered first, then animate
      const timer = setTimeout(() => {
        setAnimationPhase('phase1');
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [hasPreloadedOnMount, animationPhase]);
  
  useEffect(() => {
    // Signal ready when animation starts
    if (animationPhase === 'phase1' && !hasSignaledReadyRef.current) {
      hasSignaledReadyRef.current = true;
      onReady?.();
    }
  }, [animationPhase, onReady]);

  // Handle phase transitions with separate effect
  useEffect(() => {
    if (animationPhase === 'phase1') {
      const phase2Timer = setTimeout(() => {
        setAnimationPhase('phase2');
      }, 800);
      return () => clearTimeout(phase2Timer);
    }
    if (animationPhase === 'phase2') {
      const completeTimer = setTimeout(() => {
        setAnimationPhase('complete');
      }, 300);
      return () => clearTimeout(completeTimer);
    }
  }, [animationPhase]);

  // Ref for the zoomed image container
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [imagePosition, setImagePosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Track the imageIds we've already loaded to avoid refetching
  const loadedImageIdsRef = useRef<string>(hasPreloadedOnMount ? imageIds.join(',') : '');

  // Combined effect: Reset state, load images (preloaded or fetch), and trigger animation
  useEffect(() => {
    if (imageIds.length > 0 && companyId) {
      const currentImageIdsKey = imageIds.join(',');
      const alreadyLoaded = loadedImageIdsRef.current === currentImageIdsKey;
      
      // Skip if we already have these images loaded (from preload or previous fetch)
      if (alreadyLoaded) {
        fetchCompanyLogo();
        checkInstagramConnection();
        return;
      }
      
      // Check if we have preloaded images that cover ALL requested imageIds
      const preloadedIdsSet = new Set(preloadedImages?.map(img => img.id) || []);
      const allIdsPreloaded = preloadedImages && preloadedImages.length > 0 && 
        imageIds.every(id => preloadedIdsSet.has(String(id)));
      
      if (allIdsPreloaded) {
        // All images are preloaded - use them directly in the correct order
        const orderedImages = imageIds
          .map(id => preloadedImages!.find(img => img.id === String(id)))
          .filter((img): img is NonNullable<typeof img> => img !== undefined);
        
        setImages(orderedImages);
        setLoading(false);
        setMainImageLoaded(true);
        setLoadedThumbnails(new Set(orderedImages.map(img => img.id)));
        setAnimationPhase('initial');
        loadedImageIdsRef.current = currentImageIdsKey;
        hasSignaledReadyRef.current = false;
        hasTriggeredPreloadAnimationRef.current = false;
        
        // Set current view image to the first image
        if (orderedImages.length > 0) {
          setCurrentViewImage(orderedImages[0]);
          setSelectedImageIds([orderedImages[0].id]);
        }
        
        fetchCompanyLogo();
        checkInstagramConnection();
        
        // Trigger animation after a small delay
        setTimeout(() => {
          setAnimationPhase('phase1');
        }, 50);
        return;
      }
      
      // Check if we have preloaded images for NEW imageIds only (generate more case)
      const existingImageIds = new Set(images.map(img => img.id));
      const newImageIds = imageIds.filter(id => !existingImageIds.has(String(id)));
      const allNewIdsPreloaded = newImageIds.length > 0 && newImageIds.every(id => preloadedIdsSet.has(String(id)));
      
      if (preloadedImages && preloadedImages.length > 0 && allNewIdsPreloaded && images.length > 0) {
        // Combine existing images with new preloaded images
        const newPreloadedImages = newImageIds
          .map(id => preloadedImages.find(img => img.id === String(id)))
          .filter((img): img is NonNullable<typeof img> => img !== undefined);
        
        const combinedImages = [...images, ...newPreloadedImages];
        
        setImages(combinedImages);
        setLoading(false);
        setMainImageLoaded(true);
        setLoadedThumbnails(new Set(combinedImages.map(img => img.id)));
        setAnimationPhase('initial');
        loadedImageIdsRef.current = currentImageIdsKey;
        hasSignaledReadyRef.current = false;
        hasTriggeredPreloadAnimationRef.current = false;
        
        // Set current view image to the first NEW image
        if (newPreloadedImages.length > 0) {
          setCurrentViewImage(newPreloadedImages[0]);
          setSelectedImageIds([newPreloadedImages[0].id]);
        }
        
        fetchCompanyLogo();
        checkInstagramConnection();
        
        // Trigger animation after a small delay
        setTimeout(() => {
          setAnimationPhase('phase1');
        }, 50);
        return;
      }
      
      // Reset signalling
      hasSignaledReadyRef.current = false;
      // Reset and fetch normally (for "generate more" or non-preloaded case)
      setAnimationPhase('initial');
      setMainImageLoaded(false);
      setLoadedThumbnails(new Set());
      loadedImageIdsRef.current = currentImageIdsKey;
      fetchGeneratedImages();
      fetchCompanyLogo();
      checkInstagramConnection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageIds.join(','), companyId, preloadedImages]);

  // Trigger animation sequence when loading completes AND images are loaded
  // Note: allThumbnailsLoaded may never become true if thumbnails are lazy-loaded inside an
  // opacity-0 container (desktop), so we also trigger on mainImageLoaded alone as a fallback.
  useEffect(() => {
    if (!loading && images.length > 0 && animationPhase === 'initial') {
      if (mainImageLoaded && allThumbnailsLoaded) {
        // Ideal: all images ready
        requestAnimationFrame(() => {
          setAnimationPhase('phase1');
        });
      } else if (mainImageLoaded) {
        // Fallback: main image loaded but thumbnails may be lazy — start animation after short delay
        const fallbackTimer = setTimeout(() => {
          setAnimationPhase((prev) => (prev === 'initial' ? 'phase1' : prev));
        }, 150);
        return () => clearTimeout(fallbackTimer);
      }
    }
  }, [loading, images.length, mainImageLoaded, allThumbnailsLoaded, animationPhase]);

  // Set the first image as current view image when images load
  useEffect(() => {
    if (images.length > 0 && !currentViewImage) {
      setCurrentViewImage(images[0]);
    }
  }, [images, currentViewImage]);

  // Select the first image by default when images load and nothing is selected yet
  useEffect(() => {
    if (images.length > 0 && selectedImageIds.length === 0) {
      setSelectedImageIds([images[0].id]);
    }
  }, [images, selectedImageIds]);

  // Refresh signed URLs every 45 minutes to prevent expiration (URLs expire after 1 hour)
  useEffect(() => {
    if (images.length === 0 || !companyId) return;

    const REFRESH_INTERVAL_MS = 45 * 60 * 1000; // 45 minutes

    const refreshSignedUrls = async () => {
      try {
        const filePaths = images.map(img => `${companyId}/full/${img.fileName}`);
        const { data, error } = await supabase.storage
          .from('generadas')
          .createSignedUrls(filePaths, 3600);

        if (error || !data) {
          console.warn('Error refreshing signed URLs:', error);
          return;
        }

        const urlMap = new Map<string, string>();
        data.forEach((item, index) => {
          if (item.signedUrl) {
            urlMap.set(images[index].id, item.signedUrl);
            setCachedSignedUrl(filePaths[index], item.signedUrl);
          }
        });

        if (urlMap.size > 0) {
          setImages(prev =>
            prev.map(img => {
              const newUrl = urlMap.get(img.id);
              return newUrl ? { ...img, signedUrl: newUrl } : img;
            })
          );
          // Also update currentViewImage if it's one of the refreshed images
          setCurrentViewImage(prev => {
            if (!prev) return prev;
            const newUrl = urlMap.get(prev.id);
            return newUrl ? { ...prev, signedUrl: newUrl } : prev;
          });
        }
      } catch (err) {
        console.warn('Error refreshing signed URLs:', err);
      }
    };

    const intervalId = setInterval(refreshSignedUrls, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [images.length, companyId]);

  // Reset zoom level and position when fullscreen image changes
  useEffect(() => {
    if (fullScreenImage) {
      setZoomLevel(100);
      setImagePosition({ x: 0, y: 0 });
    }
  }, [fullScreenImage]);

  // Reset Instagram post status when closing the Instagram preview
  useEffect(() => {
    if (!showInstagramPreview) {
      setInstagramPostSuccess(false);
    }
  }, [showInstagramPreview]);

  const checkInstagramConnection = async () => {
    try {
      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) {
        setIsInstagramConnected(false);
        return;
      }

      // Check instagram_data in public schema keyed by id_company
      if (companyId) {
        const { data: instagramData } = await supabase
          .from('instagram_data')
          .select('instagram_username')
          .eq('id_company', companyId)
          .maybeSingle();

        if (instagramData?.instagram_username) {
          setIsInstagramConnected(true);
          return;
        }
      }

      setIsInstagramConnected(false);
    } catch (e) {
      console.error('Error checking Instagram connection:', e);
      setIsInstagramConnected(false);
    }
  };

  // Create optimized version of an image (same as full modal)
  const createOptimizedImage = async (imageUrl: string, fileName: string) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Failed to get canvas context');

      const resizePromise = new Promise<Blob>((resolve, reject) => {
        img.onload = () => {
          const targetWidth = 300;
          const targetHeight = 300;
          let width = img.width;
          let height = img.height;
          if (width > height) {
            if (width > targetWidth) {
              height = Math.round(height * (targetWidth / width));
              width = targetWidth;
            }
          } else {
            if (height > targetHeight) {
              width = Math.round(width * (targetHeight / height));
              height = targetHeight;
            }
          }
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('Failed to create image blob'))),
            'image/jpeg',
            0.8
          );
        };
        img.onerror = () => reject(new Error('Failed to load image for optimization'));
        img.src = URL.createObjectURL(blob);
      });

      const optimizedBlob = await resizePromise; // eslint-disable-line @typescript-eslint/no-unused-vars
      const optFilePath = `${companyId}/opt/${fileName}`;
      const { error: optUploadError } = await supabase.storage.from('generadas').upload(optFilePath, optimizedBlob);
      if (optUploadError) {
        console.error('Error uploading optimized image:', optUploadError);
      }
      URL.revokeObjectURL(img.src);
    } catch (err) {
      console.error('Error creating optimized image:', err);
    }
  };

  const fetchGeneratedImages = async () => {
    console.log('[GeneratedImagesInline] fetchGeneratedImages called with imageIds:', imageIds, 'companyId:', companyId);
    if (!companyId || imageIds.length === 0) return;
    try {
      setError(null);
      setLoading(true);

      // Deduplicate and preserve order
      const uniqueIds = Array.from(new Set(imageIds));
      const resultsMap = new Map<number | string, GeneratedImage>();
      const pending = new Set<number | string>(uniqueIds);

      const hasVideoIds = uniqueIds.some(id => typeof id === 'string' && !/^\d+$/.test(String(id)));
      const maxAttempts = hasVideoIds ? 30 : 8; // Videos need longer polling (~90s vs ~8s)
      const retryDelay = hasVideoIds ? 3000 : 1000;
      const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

      for (let attempt = 1; attempt <= maxAttempts && pending.size > 0; attempt++) {
        for (const imageId of Array.from(pending)) {
          try {
            const { data: imageData, error: imageError } = await supabase
              .from('imagenes_generadas')
              .select('file_name, favorito, created_at, tipo, variante_id, variantes:variantes(nombre)')
              .eq('id', imageId)
              .eq('id_company', companyId)
              .single();

            if (imageError) {
              console.warn(`[fetchGeneratedImages] Intento ${attempt}: error obteniendo imagen ${imageId}:`, imageError);
              continue;
            }

            console.log(`[fetchGeneratedImages] Intento ${attempt}: imageData for ${imageId}:`, { file_name: imageData?.file_name, tipo: imageData?.tipo });

            const hasFileName = imageData?.file_name !== null && imageData?.file_name !== undefined;
            if (!hasFileName) {
              console.log(`[fetchGeneratedImages] Intento ${attempt}: no file_name yet for ${imageId}`);
              continue;
            }

            const filePath = `${companyId}/full/${imageData.file_name}`;
            const { data: signedUrlData, error: signedUrlError } = await supabase.storage.from('generadas').createSignedUrl(filePath, 3600);
            if (!signedUrlData?.signedUrl) {
              console.log(`[fetchGeneratedImages] Intento ${attempt}: no signedUrl for ${imageId}, path: ${filePath}`, signedUrlError);
              continue;
            }
            setCachedSignedUrl(filePath, signedUrlData.signedUrl);

            const isVideo = imageData.tipo === 'video' || imageData.tipo === 'video_pro' || /\.(mp4|webm|mov)$/i.test(imageData.file_name);

            // For videos, try to fetch a .png thumbnail for cards
            let optSignedUrl: string | undefined;
            if (isVideo) {
              const thumbnailFileName = imageData.file_name.replace(/\.mp4$/i, '.png');
              const thumbPath = `${companyId}/opt/${thumbnailFileName}`;
              const cachedThumb = getCachedSignedUrl(thumbPath);
              if (cachedThumb) {
                optSignedUrl = cachedThumb;
              } else {
                const { data: thumbData } = await supabase.storage.from('generadas').createSignedUrl(thumbPath, 3600);
                if (thumbData?.signedUrl) {
                  optSignedUrl = thumbData.signedUrl;
                  setCachedSignedUrl(thumbPath, optSignedUrl);
                }
              }
            }

            resultsMap.set(imageId, {
              id: String(imageId),
              fileName: imageData.file_name,
              signedUrl: signedUrlData.signedUrl,
              optSignedUrl,
              favorito: imageData?.favorito === true,
              isVideo,
              created_at: imageData?.created_at,
              variante_nombre: imageData?.variante_id ? ((imageData as any).variantes?.nombre || 'Imagen') : 'Imagen',
            });

            // Crear versión optimizada en segundo plano (skip for videos)
            if (!isVideo) {
              createOptimizedImage(signedUrlData.signedUrl, imageData.file_name).catch((err) => console.error('Error creating optimized image:', err));
            }

            pending.delete(imageId);
          } catch (innerErr) {
            console.error(`Intento ${attempt}: error inesperado para ${imageId}`, innerErr);
          }
        }

        // Actualizar incrementando y mantener el orden de entrada
        const ordered = uniqueIds
          .map((id) => resultsMap.get(id))
          .filter((v): v is GeneratedImage => Boolean(v));
        if (ordered.length > 0) {
          setImages(ordered);
          // En cuanto haya al menos una imagen, dejar de bloquear el UI del padre
          setLoading(false);
        }

        if (pending.size > 0) {
          await delay(retryDelay);
        }
      }

      if (pending.size > 0) {
        console.warn('Algunas imágenes aún no están listas o no se pudieron cargar:', Array.from(pending));
      }

      // Actualización final para asegurar estado consistente
      const finalOrdered = uniqueIds
        .map((id) => resultsMap.get(id))
        .filter((v): v is GeneratedImage => Boolean(v));
      setImages(finalOrdered);
    } catch (err) {
      console.error('Error fetching generated images:', err);
      setError('Error al cargar las imágenes generadas. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch history images with pagination (excluding current batch)
  const HISTORY_PAGE_SIZE = 20;
  const fetchHistoryImages = async (offset = 0) => {
    if (!companyId) return;
    const isLoadMore = offset > 0;
    try {
      if (isLoadMore) {
        setLoadingMoreHistory(true);
      } else {
        setLoadingHistory(true);
      }
      const currentImageIdNumbers = imageIds;

      const { data: imagesData, error: imagesError } = await supabase
        .from('imagenes_generadas')
        .select('id, file_name, favorito, tipo, created_at, variante_id, variantes:variantes(nombre)')
        .eq('id_company', companyId)
        .eq('active', true)
        .not('id', 'in', `(${currentImageIdNumbers.join(',')})`)
        .order('created_at', { ascending: false })
        .range(offset, offset + HISTORY_PAGE_SIZE - 1);

      if (imagesError) {
        console.error('Error fetching history images:', imagesError);
        return;
      }

      // If fewer results than page size, no more pages
      if (!imagesData || imagesData.length < HISTORY_PAGE_SIZE) {
        setHasMoreHistory(false);
      }

      const processed: GeneratedImage[] = [];
      for (const img of (imagesData || [])) {
        if (!img.file_name) continue;
        const isVideo = img.tipo === 'video' || img.tipo === 'video_pro';
        const fileName = img.file_name;
        const thumbnailFileName = isVideo ? fileName.replace(/\.mp4$/i, '.png') : fileName;
        // Check signed URL cache first, then fetch from Supabase if needed
        let optSignedUrl = '';
        const optPath = `${companyId}/opt/${thumbnailFileName}`;
        const fullPath = `${companyId}/full/${fileName}`;
        const cachedOpt = getCachedSignedUrl(optPath);
        if (cachedOpt) {
          optSignedUrl = cachedOpt;
        } else {
          const { data: optData } = await supabase.storage.from('generadas').createSignedUrl(optPath, 3600);
          if (optData?.signedUrl) {
            optSignedUrl = optData.signedUrl;
            setCachedSignedUrl(optPath, optSignedUrl);
          } else {
            const cachedFull = getCachedSignedUrl(fullPath);
            if (cachedFull) {
              optSignedUrl = cachedFull;
            } else {
              const { data: fullData } = await supabase.storage.from('generadas').createSignedUrl(fullPath, 3600);
              if (fullData?.signedUrl) {
                optSignedUrl = fullData.signedUrl;
                setCachedSignedUrl(fullPath, optSignedUrl);
              }
            }
          }
        }
        if (!optSignedUrl && !isVideo) continue;
        processed.push({
          id: String(img.id),
          fileName: img.file_name,
          signedUrl: optSignedUrl,
          optSignedUrl,
          favorito: img.favorito === true,
          isVideo,
          created_at: img.created_at,
          variante_nombre: img.variante_id ? ((img as any).variantes?.nombre || 'Imagen') : 'Imagen',
        });
      }
      if (isLoadMore) {
        setHistoryImages(prev => [...prev, ...processed]);
      } else {
        setHistoryImages(processed);
      }
    } catch (err) {
      console.error('Error fetching history images:', err);
    } finally {
      setLoadingHistory(false);
      setLoadingMoreHistory(false);
    }
  };

  // Load more history when scrolling to bottom of grid
  const handleGridScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasMoreHistory || loadingMoreHistory || loadingHistory) return;
    const el = e.currentTarget;
    // Trigger when within 200px of bottom
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
      fetchHistoryImages(historyImages.length);
    }
  };

  // Fetch history on mount
  useEffect(() => {
    if (companyId && imageIds.length > 0) {
      fetchHistoryImages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Reverse hero animation: detail → grid
  const handleBackToGrid = () => {
    if (mobileView !== 'detail') return; // only from detail view
    if (heroAnimating) return; // prevent double-trigger
    setShowMoreOptions(false);
    // Measure the actual rendered image element in the detail view
    // Find the currently visible snap item's img (the one that's scrolled into view)
    const scrollEl = scrollContainerRef.current;
    let detailImg: HTMLImageElement | null = null;
    if (scrollEl) {
      const snapItems = scrollEl.children;
      const scrollLeft = scrollEl.scrollLeft;
      const itemWidth = scrollEl.clientWidth;
      const activeIndex = Math.round(scrollLeft / itemWidth);
      if (snapItems[activeIndex]) {
        detailImg = snapItems[activeIndex].querySelector('img') as HTMLImageElement | null;
      }
    }
    const imgRect = detailImg?.getBoundingClientRect();
    if (imgRect && currentViewImage && imgRect.width > 0) {
      const startRect = { x: imgRect.x, y: imgRect.y, width: imgRect.width, height: imgRect.height };
      setHeroRect(startRect);
      setHeroDirection('reverse');
      setHeroPhase('start');
      setHeroAnimating(true);
      // First frame: overlay renders on top of detail view at the exact image position
      requestAnimationFrame(() => {
        // Now switch to grid view — the overlay covers the transition
        setMobileView('grid');
        requestAnimationFrame(() => {
          // Scroll the current image's thumbnail into view (handles swipe to different image)
          const thumbEl = document.querySelector(`[data-image-id="${currentViewImage!.id}"]`);
          if (thumbEl && mobileScrollRef.current) {
            const container = mobileScrollRef.current;
            const thumbRect = thumbEl.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            // Check if thumbnail is outside the visible area
            const isAbove = thumbRect.top < containerRect.top;
            const isBelow = thumbRect.bottom > containerRect.bottom;
            if (isAbove || isBelow) {
              // Scroll so the thumbnail is centered vertically in the container
              const thumbCenter = thumbRect.top + thumbRect.height / 2;
              const containerCenter = containerRect.top + containerRect.height / 2;
              container.scrollTop += thumbCenter - containerCenter;
            }
          } else if (mobileScrollRef.current) {
            // Fallback: restore saved scroll position
            mobileScrollRef.current.scrollTop = savedGridScrollTop.current;
          }
          requestAnimationFrame(() => {
            // Find the target thumbnail and animate to it
            const thumbElFinal = document.querySelector(`[data-image-id="${currentViewImage!.id}"]`);
            if (thumbElFinal) {
              const thumbRect = thumbElFinal.getBoundingClientRect();
              setHeroTargetRect({ x: thumbRect.x, y: thumbRect.y, width: thumbRect.width, height: thumbRect.height });
            }
            setHeroPhase('end');
            setTimeout(() => {
              setHeroAnimating(false);
              setHeroRect(null);
              setHeroTargetRect(null);
              setHeroDirection('forward');
            }, 420);
          });
        });
      });
    } else {
      // Fallback: no animation
      setMobileView('grid');
    }
  };

  // Keep a ref to always have the latest handleBackToGrid (avoids stale closures in popstate)
  const handleBackToGridRef = useRef(handleBackToGrid);
  handleBackToGridRef.current = handleBackToGrid;

  // Listen for popstate to handle back from detail → grid
  useEffect(() => {
    const handlePopState = () => {
      handleBackToGridRef.current();
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Hero animation: Apple Photos style — detail view shows immediately,
  // image overlay animates from thumbnail rect to its final position in the viewer
  const heroImageRef = useRef<HTMLDivElement>(null);
  const handleGridImageTap = (image: GeneratedImage, event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHeroRect({ x: rect.x, y: rect.y, width: rect.width, height: rect.height });
    // Capture natural image dimensions for contain-fit calculation
    const imgEl = event.currentTarget.querySelector('img');
    if (imgEl && imgEl.naturalWidth && imgEl.naturalHeight) {
      heroNaturalSize.current = { w: imgEl.naturalWidth, h: imgEl.naturalHeight };
    } else {
      heroNaturalSize.current = null;
    }
    setCurrentViewImage(image);
    setSelectedImageIds([image.id]);
    // Lazily fetch full-resolution URL if we only have the opt version
    if (image.optSignedUrl && image.signedUrl === image.optSignedUrl) {
      const fullPath = `${companyId}/full/${image.fileName}`;
      const cachedFull = getCachedSignedUrl(fullPath);
      if (cachedFull) {
        setCurrentViewImage(prev => prev && prev.id === image.id ? { ...prev, signedUrl: cachedFull } : prev);
        setImages(prev => prev.map(img => img.id === image.id ? { ...img, signedUrl: cachedFull } : img));
        setHistoryImages(prev => prev.map(img => img.id === image.id ? { ...img, signedUrl: cachedFull } : img));
      } else {
        supabase.storage.from('generadas').createSignedUrl(fullPath, 3600).then(({ data }) => {
          if (data?.signedUrl) {
            const fullUrl = data.signedUrl;
            setCachedSignedUrl(fullPath, fullUrl);
            setCurrentViewImage(prev => prev && prev.id === image.id ? { ...prev, signedUrl: fullUrl } : prev);
            setImages(prev => prev.map(img => img.id === image.id ? { ...img, signedUrl: fullUrl } : img));
            setHistoryImages(prev => prev.map(img => img.id === image.id ? { ...img, signedUrl: fullUrl } : img));
          }
        });
      }
    }
    setHeroPhase('start');
    setHeroDirection('forward');
    setHeroAnimating(true);
    // Save grid scroll position before switching views
    if (mobileScrollRef.current) {
      savedGridScrollTop.current = mobileScrollRef.current.scrollTop;
    }
    // Switch to detail view immediately — buttons are visible from the start
    setMobileView('detail');
    // Push history state so back button returns to grid
    window.history.pushState({ mobileDetailView: true }, '');
    // After detail view paints, measure the image viewer area and transition
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setHeroPhase('end');
        // After transition completes, remove overlay to reveal the real detail view image
        setTimeout(() => {
          setHeroAnimating(false);
          setHeroRect(null);
        }, 420);
      });
    });
  };

  // Scroll-snap based swipe: detect which image the user scrolled to
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleDetailScroll = () => {
    if (isScrollProgrammatic.current) return;
    // Debounce: wait for scroll to settle
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      const container = scrollContainerRef.current;
      if (!container) return;
      const scrollLeft = container.scrollLeft;
      const width = container.clientWidth;
      const index = Math.round(scrollLeft / width);
      if (index >= 0 && index < allGridImages.length) {
        const img = allGridImages[index];
        if (img.id !== currentViewImage?.id) {
          setCurrentViewImage(img);
          setSelectedImageIds([img.id]);
        }
      }
    }, 80);
  };

  // Scroll to correct image when currentViewImage changes (e.g. from grid tap)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !currentViewImage || mobileView !== 'detail') return;
    const index = allGridImages.findIndex(img => img.id === currentViewImage.id);
    if (index === -1) return;
    const targetScroll = index * container.clientWidth;
    if (Math.abs(container.scrollLeft - targetScroll) > 2) {
      isScrollProgrammatic.current = true;
      container.scrollTo({ left: targetScroll, behavior: 'instant' });
      requestAnimationFrame(() => {
        isScrollProgrammatic.current = false;
      });
    }
  }, [currentViewImage, mobileView, allGridImages, showCropModal]);

  const handleSaveSelected = async () => {
    const imagesToSave = allGridImages.filter((img) => selectedImageIds.includes(img.id));
    if (imagesToSave.length === 0) return;
    setIsDownloading(true);
    setDownloadSuccess(false);
    await saveImages(imagesToSave);
    setDownloadSuccess(true);
    setTimeout(() => {
      setIsDownloading(false);
      setDownloadSuccess(false);
    }, 3000);
  };

  const saveImages = async (imagesToSave: GeneratedImage[]) => {

    const inferExtFromMime = (mime?: string): string => {
      if (!mime) return '';
      const m = mime.toLowerCase();
      if (m === 'image/png') return 'png';
      if (m === 'image/jpeg') return 'jpg';
      if (m === 'image/webp') return 'webp';
      if (m === 'image/gif') return 'gif';
      if (m === 'image/bmp') return 'bmp';
      if (m === 'image/tiff') return 'tiff';
      if (m === 'image/svg+xml') return 'svg';
      return '';
    };

    const sanitizeDownloadFileName = (original: string, mime?: string): string => {
      let name = (original || 'imagen').trim();
      // Fix cases like filename.png_ or filename.jpg_
      name = name.replace(/\.(png|jpe?g|webp|gif|bmp|tiff|svg)_+$/i, '.$1');
      // Remove any trailing underscores just in case
      name = name.replace(/_+$/, '');
      // Ensure we end with a known image extension; if missing, infer from MIME or default to png
      const hasKnownExt = /\.(png|jpe?g|webp|gif|bmp|tiff|svg)$/i.test(name);
      if (!hasKnownExt) {
        const ext = inferExtFromMime(mime) || 'png';
        // If there is already some extension, keep it; otherwise append inferred
        if (!/\.[a-z0-9]+$/i.test(name)) {
          name = `${name}.${ext}`;
        }
      }
      return name;
    };

    for (const image of imagesToSave) {
      try {
        const response = await fetch(image.signedUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = sanitizeDownloadFileName(image.fileName, blob.type);
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        try {
          await supabase
            .from('imagenes_generadas')
            .update({ descargada: true })
            .eq('id', image.id)
            .eq('id_company', companyId);
        } catch {}
      } catch (error) {
        console.error(`Error downloading image ${image.fileName}:`, error);
      }
    }
  };

  const handlePostSelected = async () => {
    if (selectedImageIds.length === 0) return;
    const selectedImageId = selectedImageIds[0];
    const selectedImage = images.find((img) => img.id === selectedImageId);
    if (selectedImage) setCurrentViewImage(selectedImage);
    setShowInstagramPreview(true);
    if (!instagramPostSuccess) await generateDescription();
  };

  const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const getBillingCycleRange = (paidPlanStartDate: string) => {
    const now = new Date();
    const subscriptionDate = new Date(paidPlanStartDate);
    const anchorDay = subscriptionDate.getDate();
    const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const anchorFor = (year: number, month: number) => {
      const dim = daysInMonth(year, month);
      const day = Math.min(anchorDay, dim);
      return new Date(year, month, day, 0, 0, 0, 0);
    };
    let start = anchorFor(now.getFullYear(), now.getMonth());
    if (start.getTime() > now.getTime()) {
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      start = anchorFor(prevMonth.getFullYear(), prevMonth.getMonth());
    }
    if (start.getTime() < subscriptionDate.getTime()) {
      start = new Date(subscriptionDate.getFullYear(), subscriptionDate.getMonth(), subscriptionDate.getDate(), 0, 0, 0, 0);
    }
    const nextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const end = anchorFor(nextMonth.getFullYear(), nextMonth.getMonth());
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const computeRenewalDate = (paidStart: string | null, upgradeDate?: string | null): Date | null => {
    if (!paidStart) return new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1);
    const now = new Date();
    const ref = upgradeDate ? new Date(upgradeDate) : new Date(paidStart);
    const anchorDay = ref.getDate();
    const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
    const anchorFor = (y: number, m: number) => new Date(y, m, Math.min(anchorDay, daysInMonth(y, m)));
    let start = anchorFor(now.getFullYear(), now.getMonth());
    if (start.getTime() > now.getTime()) {
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      start = anchorFor(prev.getFullYear(), prev.getMonth());
    }
    const next = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    return anchorFor(next.getFullYear(), next.getMonth());
  };

  const showInsufficientImagesModal = (action: string, required: number, remaining: number, companyProfile: any) => {
    const paidStart = companyProfile?.paid_plan_start_date as string | null;
    const upgradeDate = companyProfile?.upgrade_plan_date as string | null;
    setInsufficientModal({ action, required, remaining, renewalDate: computeRenewalDate(paidStart, upgradeDate) });
  };

  const checkSubscriptionLimitsForEdit = async (): Promise<boolean> => {
    try {
      if (!companyId) return true;
      const { data: companyProfile } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes, paid_plan_start_date, upgrade_plan_date')
        .eq('id_company', companyId)
        .single();
      if (!companyProfile) return true;
      // Block edit feature for 'emprendimiento' plan
      if ((companyProfile as any).plan === 'emprendimiento') {
        setShowEditPlanRestriction(true);
        return false;
      }
      const remaining = (companyProfile as any).imagenes_restantes as number | null | undefined;
      if (typeof remaining === 'number' && remaining < 1) {
        showInsufficientImagesModal('editar imagen', 1, remaining, companyProfile);
        return false;
      }
      return true;
    } catch {
      return true;
    }
  };

  const handleEditSelected = async () => {
    const targetId = selectedImageIds[0] ?? currentViewImage?.id;
    if (!targetId) return;
    const canEdit = await checkSubscriptionLimitsForEdit();
    if (!canEdit) return;
    try { trackInteraction('imagenes_editadas'); } catch {}
    // Pass loaded images to avoid refetching when returning
    const cachedImages = images.map(img => ({
      id: img.id,
      fileName: img.fileName,
      signedUrl: img.signedUrl,
      favorito: img.favorito,
    }));
    navigate(
      `/redes-sociales/editar-imagen?id=${targetId}&companyId=${companyId}`,
      {
        state: {
          returnToResults: true,
          resultsImageIds: imageIds,
          resultsCompanyId: companyId,
          cachedImages,
        },
      }
    );
  };

  const handleFavoriteCurrent = async () => {
    const targetId = currentViewImage?.id ?? selectedImageIds[0];
    if (!targetId) return;
    setIsFavoriting(true);
    try {
      // Use currentViewImage directly for current state (works for both latest and history images)
      const currentFav = currentViewImage?.favorito ?? images.find((img) => img.id === targetId)?.favorito;
      const nextValue = !(currentFav === true);
      const { error } = await supabase
        .from('imagenes_generadas')
        .update({ favorito: nextValue })
        .eq('id', targetId)
        .eq('id_company', companyId);
      if (error) {
        console.error('Error marcando como favorito:', error);
        alert('No se pudo marcar como favorito');
      } else {
        // Update local state (both images and historyImages)
        setImages((prev) => prev.map((img) => (img.id === targetId ? { ...img, favorito: nextValue } : img)));
        setHistoryImages((prev) => prev.map((img) => (img.id === targetId ? { ...img, favorito: nextValue } : img)));
        setCurrentViewImage((prev) => (prev && prev.id === targetId ? { ...prev, favorito: nextValue } : prev));
        // Show success message briefly when marking as favorite
        if (nextValue) {
          setShowFavoriteMessage(true);
          setTimeout(() => setShowFavoriteMessage(false), 4000);
        }
      }
    } catch (e) {
      console.error('Error marcando como favorito:', e);
      alert('No se pudo marcar como favorito');
    } finally {
      setIsFavoriting(false);
    }
  };

  // Toggle favorite on any image (used in grid view)
  const handleFavoriteImage = async (image: GeneratedImage) => {
    if (isFavoriting) return;
    setIsFavoriting(true);
    try {
      const nextValue = !(image.favorito === true);
      const { error: favError } = await supabase
        .from('imagenes_generadas')
        .update({ favorito: nextValue })
        .eq('id', image.id)
        .eq('id_company', companyId);
      if (favError) {
        console.error('Error marcando como favorito:', favError);
      } else {
        setImages((prev) => prev.map((img) => (img.id === image.id ? { ...img, favorito: nextValue } : img)));
        setHistoryImages((prev) => prev.map((img) => (img.id === image.id ? { ...img, favorito: nextValue } : img)));
        setCurrentViewImage((prev) => (prev && prev.id === image.id ? { ...prev, favorito: nextValue } : prev));
      }
    } catch (e) {
      console.error('Error marcando como favorito:', e);
    } finally {
      setIsFavoriting(false);
    }
  };

  // Reset description panel when changing image
  useEffect(() => {
    setShowDescriptionPanel(false);
    setIsGeneratingDescription(false);
    setGeneratedDescription('');
    setCopiedDescription(false);
  }, [currentViewImage?.id]);

  // Trigger progress bar animation when generating description
  useEffect(() => {
    if (showDescriptionPanel && isGeneratingDescription) {
      setDescriptionProgressActive(false);
      const raf = requestAnimationFrame(() => setDescriptionProgressActive(true));
      return () => cancelAnimationFrame(raf);
    } else {
      setDescriptionProgressActive(false);
    }
  }, [showDescriptionPanel, isGeneratingDescription]);

  // Poll DB for post_description after webhook returns success
  const waitForPostDescription = async (
    idCompany: string,
    imageId: number,
    maxWaitMs = 15000,
    intervalMs = 800
  ): Promise<string | null> => {
    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
      try {
        if (!currentViewImage || currentViewImage.id !== imageId) return null;
        const { data: imageData } = await supabase
          .from('imagenes_generadas')
          .select('post_description')
          .eq('id', imageId)
          .eq('id_company', idCompany)
          .single();
        const desc = (imageData as any)?.post_description as string | undefined;
        if (desc && desc.trim().length > 0) {
          return desc;
        }
      } catch {}
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    return null;
  };

  // Pre-flight: check limits, fetch remaining, show confirmation modal
  const handleCarruselClick = async () => {
    if (!currentViewImage || isGeneratingNewAngles) return;
    setShowMoreOptions(false);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { alert('No se pudo identificar al usuario'); return; }

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      const userCompanyId = profile?.id_company || companyId;
      if (userCompanyId) {
        const { data: companyProfile } = await supabase
          .from('company_profiles')
          .select('plan, imagenes_restantes, paid_plan_start_date, upgrade_plan_date')
          .eq('id_company', userCompanyId)
          .single();

        const remaining = (companyProfile as any)?.imagenes_restantes as number | null | undefined;
        if (typeof remaining === 'number' && remaining < 2) {
          showInsufficientImagesModal('generar carrusel', 2, remaining, companyProfile);
          return;
        }
      }
    } catch {
      // subscription check failed, allow modal to show anyway
    }

    setShowCarruselConfirm(true);
  };

  // Handle generating new angles from current image — shows placeholder cards in grid
  const handleGenerateNewAngles = async () => {
    if (!currentViewImage || isGeneratingNewAngles) return;
    const sourceImageId = currentViewImage.id;
    setShowCarruselConfirm(false);
    setIsGeneratingNewAngles(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('No se pudo identificar al usuario');
        setIsGeneratingNewAngles(false);
        return;
      }

      // Switch to grid view and show placeholder cards
      setMobileView('grid');
      setGeneratingPlaceholderCount(2);

      // Scroll to top so user sees the placeholders
      requestAnimationFrame(() => {
        if (mobileScrollRef.current) mobileScrollRef.current.scrollTop = 0;
      });

      // Fire API call in the background
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/generar_nuevos_angulos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify({ user_id: user.id, image_id: sourceImageId }),
      });
      let newImageIds: string[] = [];
      try {
        const data = await response.json();
        if (Array.isArray(data) && data[0]?.imagenes_generadas_ids) {
          newImageIds = data[0].imagenes_generadas_ids;
        } else if (data?.imagenes_generadas_ids) {
          newImageIds = data.imagenes_generadas_ids;
        }
      } catch {
        newImageIds = [];
      }

      if (newImageIds.length === 0) {
        setGeneratingPlaceholderCount(0);
        setIsGeneratingNewAngles(false);
        alert('No se pudo generar nuevos ángulos. Intentá nuevamente.');
        return;
      }

      // Fetch the new images (with retries)
      const newImages: GeneratedImage[] = [];
      const maxAttempts = 12;
      const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        for (const imageId of newImageIds) {
          if (newImages.some(img => String(img.id) === String(imageId))) continue;
          try {
            const { data: imageData } = await supabase
              .from('imagenes_generadas')
              .select('file_name, favorito')
              .eq('id', imageId)
              .eq('id_company', companyId)
              .single();
            if (!imageData?.file_name) continue;
            const filePath = `${companyId}/full/${imageData.file_name}`;
            const { data: signedUrlData } = await supabase.storage.from('generadas').createSignedUrl(filePath, 3600);
            if (!signedUrlData?.signedUrl) continue;
            newImages.push({
              id: imageId,
              fileName: imageData.file_name,
              signedUrl: signedUrlData.signedUrl,
              favorito: imageData?.favorito === true,
            });
            createOptimizedImage(signedUrlData.signedUrl, imageData.file_name).catch(() => {});
          } catch {}
        }
        if (newImages.length === newImageIds.length) break;
        await delay(1000);
      }

      // Replace placeholders with real images
      setGeneratingPlaceholderCount(0);
      if (newImages.length > 0) {
        setImages(prev => [...newImages, ...prev]);
      }
      setIsGeneratingNewAngles(false);
    } catch (_e) {
      setGeneratingPlaceholderCount(0);
      setIsGeneratingNewAngles(false);
      alert('No se pudo generar nuevos ángulos. Intentá nuevamente.');
    }
  };

  // Handle "Regenerar" inline — stay in grid with placeholder cards
  const handleRegenerateInline = async () => {
    if (!onGenerateMore || isGeneratingMore) return;
    setShowMoreOptions(false);
    setIsGeneratingMore(true);
    try { trackInteraction('generar_mas'); } catch {}

    // Switch to grid and show placeholders BEFORE the API call
    setMobileView('grid');
    setGeneratingPlaceholderCount(2);
    requestAnimationFrame(() => {
      if (mobileScrollRef.current) mobileScrollRef.current.scrollTop = 0;
    });

    try {
      const result = await onGenerateMore();
      // If the parent returned image IDs, fetch and display them
      if (result && result.imageIds && result.imageIds.length > 0) {
        const returnedCompanyId = result.companyId || companyId;

        // Fetch the new images with retries
        const newImages: GeneratedImage[] = [];
        const maxAttempts = 15;
        const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          for (const imageId of result.imageIds) {
            if (newImages.some(img => String(img.id) === String(imageId))) continue;
            try {
              const { data: imageData } = await supabase
                .from('imagenes_generadas')
                .select('file_name, favorito')
                .eq('id', imageId)
                .eq('id_company', returnedCompanyId)
                .single();
              if (!imageData?.file_name) continue;
              const filePath = `${returnedCompanyId}/full/${imageData.file_name}`;
              const { data: signedUrlData } = await supabase.storage.from('generadas').createSignedUrl(filePath, 3600);
              if (!signedUrlData?.signedUrl) continue;
              newImages.push({
                id: String(imageId),
                fileName: imageData.file_name,
                signedUrl: signedUrlData.signedUrl,
                favorito: imageData?.favorito === true,
              });
              createOptimizedImage(signedUrlData.signedUrl, imageData.file_name).catch(() => {});
            } catch {}
          }
          if (newImages.length === result.imageIds.length) break;
          await delay(1500);
        }

        setGeneratingPlaceholderCount(0);
        if (newImages.length > 0) {
          setImages(prev => [...newImages, ...prev]);
        }
      } else {
        // API failed or returned nothing — remove placeholders
        setGeneratingPlaceholderCount(0);
      }
    } catch (_e) {
      setGeneratingPlaceholderCount(0);
    } finally {
      setIsGeneratingMore(false);
    }
  };

  // Video menu helpers
  const openVideoMenu = async () => {
    if (!currentViewImage) return;
    // Check plan: only pro (negocio) and ultra (enterprise) can generate video
    try {
      const { data: companyProfile } = await supabase
        .from('company_profiles')
        .select('plan')
        .eq('id_company', companyId)
        .single();
      const plan = (companyProfile as any)?.plan;
      if (plan && plan !== 'negocio' && plan !== 'enterprise') {
        setShowMoreOptions(false);
        setShowVideoPlanRestriction(true);
        return;
      }
    } catch {}
    setShowMoreOptions(false);
    setVideoMode('automatico');
    setVideoStep('select');
    setVideoPrompt('');
    setVideoDuration('6');
    setShowVideoMenu(true);
    requestAnimationFrame(() => setVideoMenuAnimated(true));
  };

  const closeVideoMenu = () => {
    setVideoMenuAnimated(false);
    setTimeout(() => {
      setShowVideoMenu(false);
      setVideoMenuDragY(0);
      setVideoStep('select');
      setVideoPrompt('');
      setVideoDuration('6');
    }, 300);
  };

  // Handle generating video from current image
  const handleGenerateVideo = async () => {
    if (!currentViewImage || isGeneratingVideo) return;
    closeVideoMenu();
    setVideoProgressComplete(false);
    setIsGeneratingVideo(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('No se pudo identificar al usuario');
        setVideoProgressComplete(true);
        return;
      }

      // Check subscription limits based on selected duration (only describir mode uses custom duration)
      const effectiveDuration = videoMode === 'describir' ? videoDuration : '6';
      const videoImagesCost = effectiveDuration === '4' ? 12 : effectiveDuration === '8' ? 24 : 18;
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      const userCompanyId = profile?.id_company || companyId;
      if (userCompanyId) {
        const { data: companyProfile } = await supabase
          .from('company_profiles')
          .select('plan, imagenes_restantes, paid_plan_start_date, upgrade_plan_date')
          .eq('id_company', userCompanyId)
          .single();

        const remaining = (companyProfile as any)?.imagenes_restantes as number | null | undefined;
        if (typeof remaining === 'number' && remaining < videoImagesCost) {
          showInsufficientImagesModal('animar video', videoImagesCost, remaining, companyProfile);
          setVideoProgressComplete(true);
          return;
        }
      }

      const body: Record<string, unknown> = {
        user_id: user.id,
        image_id: currentViewImage.id,
        mode: videoMode,
        duracion: effectiveDuration,
      };
      if (videoMode === 'describir' && videoPrompt.trim()) {
        body.prompt = videoPrompt.trim();
      }

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/generar_video_desde_imagen', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify(body),
      });

      let videoId: string | null = null;
      try {
        const data = await response.json();
        if (Array.isArray(data) && data[0]?.video_id) {
          videoId = String(data[0].video_id);
        } else if (data?.video_id) {
          videoId = String(data.video_id);
        }
      } catch {
        videoId = null;
      }

      if (!videoId) {
        setVideoProgressComplete(true);
        alert('No se pudo generar el video. Intentá nuevamente.');
        return;
      }

      // Poll Supabase for the video file and add it to the gallery
      const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
      let videoAdded = false;
      for (let attempt = 1; attempt <= 15; attempt++) {
        try {
          const { data: videoData } = await supabase
            .from('imagenes_generadas')
            .select('file_name, favorito')
            .eq('id', videoId)
            .eq('id_company', companyId)
            .single();
          if (!videoData?.file_name) { await delay(3000); continue; }
          const filePath = `${companyId}/full/${videoData.file_name}`;
          const { data: signedUrlData } = await supabase.storage.from('generadas').createSignedUrl(filePath, 3600);
          if (!signedUrlData?.signedUrl) { await delay(3000); continue; }
          const newVideoItem: GeneratedImage = {
            id: videoId,
            fileName: videoData.file_name,
            signedUrl: signedUrlData.signedUrl,
            favorito: videoData.favorito === true,
            isVideo: true,
          };
          setImages(prev => [...prev, newVideoItem]);
          setCurrentViewImage(newVideoItem);
          setSelectedImageIds([videoId]);
          videoAdded = true;
          break;
        } catch { await delay(3000); }
      }
      setVideoProgressComplete(true);
      if (!videoAdded) {
        alert('El video se generó pero aún no está listo. Recargá la página en unos minutos.');
      }
    } catch (_e) {
      setVideoProgressComplete(true);
      alert('No se pudo generar el video. Intentá nuevamente.');
    }
  };

  // Handle generating Instagram description
  const handleGenerateDescription = async () => {
    if (!currentViewImage) return;
    setShowMoreOptions(false);
    setShowDescriptionPanel(true);
    setCopiedDescription(false);
    setIsGeneratingDescription(true);
    setGeneratedDescription('Generando descripción para post de Instagram');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setGeneratedDescription('No se pudo generar la descripción. Intentá nuevamente.');
        setIsGeneratingDescription(false);
        return;
      }
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
      let idCompany = (profile as any)?.id_company ?? null;
      if (!idCompany && companyId) {
        idCompany = companyId;
      }
      if (!idCompany) {
        setGeneratedDescription('No se pudo generar la descripción. Intentá nuevamente.');
        setIsGeneratingDescription(false);
        return;
      }
      // Check if description already exists
      const { data: existing } = await supabase
        .from('imagenes_generadas')
        .select('post_description')
        .eq('id', currentViewImage.id)
        .eq('id_company', idCompany)
        .single();
      if ((existing as any)?.post_description) {
        setGeneratedDescription((existing as any).post_description as string);
        setIsGeneratingDescription(false);
        return;
      }
      // Call webhook to generate description
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/descripcion_instagram', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify({ user_id: user.id, image_id: currentViewImage.id }),
      });
      let ok = false;
      try {
        const data = await response.json();
        ok = data?.response === 'success';
      } catch {
        ok = false;
      }
      if (ok) {
        const finalText = await waitForPostDescription(idCompany, currentViewImage.id);
        if (finalText) {
          setGeneratedDescription(finalText);
        } else {
          setGeneratedDescription('No se pudo obtener la descripción todavía. Probá nuevamente en unos segundos.');
        }
      } else {
        setGeneratedDescription('No se pudo generar la descripción. Intentá nuevamente.');
      }
    } catch (_e) {
      setGeneratedDescription('No se pudo generar la descripción. Intentá nuevamente.');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Copy description to clipboard
  const copyGeneratedDescription = async () => {
    try {
      await navigator.clipboard.writeText(generatedDescription);
      setCopiedDescription(true);
      setTimeout(() => setCopiedDescription(false), 2000);
    } catch (err) {
      console.error('Error copying description:', err);
    }
  };

  const fetchCompanyLogo = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
      if (profile?.id_company) {
        const { data: companyData } = await supabase
          .from('company_profiles')
          .select('logo_url, rubro')
          .eq('id_company', profile.id_company)
          .single();
        if (companyData?.logo_url) setCompanyLogo(companyData.logo_url);
        // Check if rubro is "Moda e Indumentaria"
        const rubro = (companyData as any)?.rubro as string | undefined;
        setIsFashionRubro(rubro === 'Moda e Indumentaria');
      }
    } catch (err) {
      console.error('Error fetching company logo:', err);
    }
  };

  const copyDescription = async () => {
    try {
      await navigator.clipboard.writeText(postDescription);
      alert('Descripción copiada al portapapeles');
    } catch (error) {
      console.error('Error al copiar la descripción:', error);
      alert('No se pudo copiar la descripción');
    }
  };

  const handlePostToInstagram = async () => {
    if (!currentViewImage) return;
    setIsPostingToInstagram(true);
    setInstagramPostSuccess(false);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('No se pudo identificar al usuario');
        setIsPostingToInstagram(false);
        return;
      }
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/0d2c7586-b0b6-45c8-94e3-ab1ff6968e7c', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, imagen_id: currentViewImage.id, mode: 'post' })
      });
      const data = await response.json();
      if (data.response === 'success') {
        setInstagramPostSuccess(true);
      } else {
        alert('Error al publicar en Instagram');
      }
    } catch (error) {
      console.error('Error posting to Instagram:', error);
      alert('Error al publicar en Instagram');
    } finally {
      setIsPostingToInstagram(false);
    }
  };

  const generateDescription = async () => {
    if (!currentViewImage) return;
    setIsGeneratingDescription(true);
    setPostDescription('Generando descripción...');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('No se pudo identificar al usuario');
        setPostDescription('Moda fashion . veni a visitarnos');
        setIsGeneratingDescription(false);
        return;
      }
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/0d2c7586-b0b6-45c8-94e3-ab1ff6968e7c', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, imagen_id: currentViewImage.id, mode: 'generate_description' })
      });
      const data = await response.json();
      if (data.response === 'success') {
        const { data: imageData, error } = await supabase
          .from('imagenes_generadas')
          .select('post_description')
          .eq('id', currentViewImage.id)
          .eq('id_company', companyId)
          .single();
        if (error) {
          console.error('Error fetching the description:', error);
          alert('Error al obtener la descripción generada');
          setPostDescription('Moda fashion . veni a visitarnos');
        } else if (imageData?.post_description) {
          setPostDescription(imageData.post_description);
        } else {
          setPostDescription('Moda fashion . veni a visitarnos');
        }
      } else {
        alert('Error al generar la descripción');
        setPostDescription('Moda fashion . veni a visitarnos');
      }
    } catch (error) {
      console.error('Error generating description:', error);
      alert('Error al generar la descripción');
      setPostDescription('Moda fashion . veni a visitarnos');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Zoom & drag handlers
  const handleZoomIn = () => {
    if (zoomLevel < 300) setZoomLevel((prev) => Math.min(prev + 25, 300));
  };
  const handleZoomOut = () => {
    if (zoomLevel > 50) setZoomLevel((prev) => Math.max(prev - 25, 50));
  };
  const handleResetZoom = () => {
    setZoomLevel(100);
    setImagePosition({ x: 0, y: 0 });
  };
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel > 100) setDragStart({ x: e.clientX, y: e.clientY });
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (dragStart && zoomLevel > 100) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setImagePosition((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };
  const handleMouseUp = () => setDragStart(null);

  const closeFullScreenView = () => {
    setFullScreenImage(null);
    setZoomLevel(100);
    setImagePosition({ x: 0, y: 0 });
  };

  // Instagram connect prompt portal
  const connectPrompt = showConnectPrompt && !isInstagramConnected
    ? createPortal(
        <div className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm flex items-center justify-center z-[100000] p-4" style={{ position: 'fixed' }}>
          <div className="bg-surface-card rounded-lg shadow-lg max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowConnectPrompt(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 inline-flex items-center justify-center"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center justify-center mb-4">
              <div className="w-14 h-14 rounded-md bg-neutral-100 flex items-center justify-center">
                <Instagram className="w-7 h-7 text-neutral-600" />
              </div>
            </div>
            <h3 className="text-[17px] font-semibold text-brand-black text-center mb-2">Conexión de Instagram requerida</h3>
            <p className="text-[15px] text-neutral-600 text-center mb-6">
              Para poder publicar el contenido generado, primero debes conectar la cuenta de Instagram de la empresa a Atlas.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => setShowConnectPrompt(false)} className="h-10 px-4 border border-neutral-200 rounded-md text-neutral-600 hover:bg-neutral-100 transition-colors shadow-sm text-[15px] font-medium">Cancelar</button>
              <button onClick={() => { navigate('/configuracion', { state: { section: 'instagram' } }); }} className="h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium shadow-sm transition-colors flex items-center justify-center gap-2 text-[15px]">
                <Instagram className="w-4 h-4" />
                Conectar Instagram
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  // Inline loading state: render nothing so parent overlay remains
  if (loading) return null;

  // Fullscreen image overlay (kept as overlay feature)
  if (fullScreenImage) {
    return (
      <>
        <div className="fixed inset-0 bg-brand-black/90 z-[1000] flex flex-col">
          <div className="px-4 py-3 bg-brand-black text-white flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button onClick={handleZoomOut} className="p-2 hover:bg-white/10 rounded-md" disabled={zoomLevel <= 50}>
                <ZoomOut className="w-5 h-5" />
              </button>
              <div className="text-[15px] font-medium">{zoomLevel}%</div>
              <button onClick={handleZoomIn} className="p-2 hover:bg-white/10 rounded-md" disabled={zoomLevel >= 300}>
                <ZoomIn className="w-5 h-5" />
              </button>
              <button onClick={handleResetZoom} className="p-2 hover:bg-white/10 rounded-md">
                <Maximize className="w-5 h-5" />
              </button>
            </div>
            <button onClick={closeFullScreenView} className="p-2 hover:bg-white/10 rounded-md">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center overflow-hidden" onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} ref={imageContainerRef}>
            <img src={fullScreenImage.signedUrl} alt={`Generated image ${fullScreenImage.id}`} style={{ transform: `scale(${zoomLevel / 100}) translate(${imagePosition.x}px, ${imagePosition.y}px)`, cursor: zoomLevel > 100 ? 'move' : 'default', transformOrigin: 'center', transition: dragStart ? 'none' : 'transform 0.2s ease-out' }} className="max-w-full max-h-full" />
          </div>
          <div className="px-4 py-3 bg-brand-black text-white">
            <div className="flex justify-center space-x-4">
              <button onClick={() => saveImages([fullScreenImage])} className="h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium shadow-sm flex items-center space-x-2 text-[15px]">
                <Download className="w-4 h-4" />
                <span>Guardar imagen</span>
              </button>
            </div>
          </div>
        </div>
        {connectPrompt}
      </>
    );
  }

  // Instagram preview overlay (kept as overlay feature)
  if (showInstagramPreview && currentViewImage) {
    if (instagramPostSuccess) {
      return (
        <>
          <div className="fixed inset-0 bg-surface-bg z-50 flex flex-col">
            <div className="px-4 py-1 sm:py-3 bg-surface-card shadow-sm z-20 sticky top-0 flex justify-between items-center border-b border-neutral-200">
              <h2 className="text-[15px] sm:text-[17px] font-semibold text-brand-black">Instagram</h2>
              <button onClick={() => { setShowInstagramPreview(false); setInstagramPostSuccess(false); }} className="w-8 h-8 rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 inline-flex items-center justify-center" aria-label="Cerrar">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center p-6">
              <div className="bg-surface-card rounded-lg shadow-lg p-8 max-w-md w-full flex flex-col items-center">
                <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mb-6">
                  <Check className="w-16 h-16 text-green-600" />
                </div>
                <h3 className="text-[17px] font-semibold text-brand-black mb-2">¡Publicado con éxito!</h3>
                <p className="text-[15px] text-neutral-600 text-center mb-6">Tu contenido ha sido publicado en Instagram correctamente.</p>
                <button onClick={() => { setShowInstagramPreview(false); setInstagramPostSuccess(false); }} className="h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium shadow-sm text-[15px]">Volver</button>
              </div>
            </div>
          </div>
          {connectPrompt}
        </>
      );
    }

    return (
      <>
        <div className="fixed inset-0 bg-surface-bg z-50 flex flex-col">
          <div className="px-4 py-1 sm:py-3 bg-surface-card shadow-sm z-20 sticky top-0 flex justify-between items-center border-b border-neutral-200">
            <h2 className="text-[15px] sm:text-[17px] font-semibold text-brand-black">Vista previa de Instagram</h2>
            <button onClick={() => setShowInstagramPreview(false)} className="w-8 h-8 rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 inline-flex items-center justify-center" aria-label="Cerrar">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto py-4 px-4 mt-2 relative">
            <div className="flex flex-col md:flex-row md:items-start md:justify-center md:space-x-6 max-w-5xl mx-auto pb-6">
              <div className="bg-surface-card rounded-md shadow-sm w-full max-w-sm mx-auto md:mx-0 overflow-hidden mb-6 md:mb-0 flex flex-col">
                <div className="flex items-center p-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-neutral-200 flex-shrink-0">
                    {companyLogo ? (
                      <img src={companyLogo} alt="Company logo" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-brand-primary flex items-center justify-center text-white font-semibold">
                        {instagramUsername.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-[15px] text-brand-black">{instagramUsername}</p>
                  </div>
                </div>
                <div ref={imageContainerRef} className="w-full overflow-hidden" style={{ margin: 0, padding: 0, borderRadius: 0 }}>
                  <img src={currentViewImage.signedUrl} alt="Instagram post" className="w-full" style={{ width: '100%', maxHeight: 'min(700px, calc(100dvh - 200px))', display: 'block', margin: 0, padding: 0, borderRadius: 0 }} onLoad={(e) => {
                    const img = e.target as HTMLImageElement;
                    const aspectRatio = img.naturalWidth / img.naturalHeight;
                    const container = imageContainerRef.current;
                    if (!container) return;
                    if (aspectRatio === 1) {
                      container.style.aspectRatio = '1/1';
                      img.style.objectFit = 'contain';
                    } else if (aspectRatio > 1 && aspectRatio <= 1.91) {
                      container.style.aspectRatio = aspectRatio.toString();
                      img.style.objectFit = 'contain';
                    } else if (aspectRatio < 1 && aspectRatio >= 0.8) {
                      container.style.aspectRatio = aspectRatio.toString();
                      img.style.objectFit = 'contain';
                    } else if (aspectRatio < 0.8) {
                      container.style.aspectRatio = '0.8';
                      img.style.height = '100%';
                      img.style.width = 'auto';
                      img.style.maxWidth = 'none';
                      img.style.minWidth = '100%';
                      img.style.objectFit = 'cover';
                      img.style.objectPosition = 'center';
                      const scaledWidth = img.naturalHeight * 0.8; // 4:5
                      if (scaledWidth < img.naturalWidth) {
                        img.style.objectPosition = '50% 50%';
                      }
                    } else {
                      container.style.aspectRatio = '1.91';
                      img.style.objectFit = 'contain';
                    }
                  }} />
                </div>
                <div className="p-3 border-t border-neutral-200 mt-auto">
                  <div className="flex space-x-4">
                    <button className="text-brand-black">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
                      </svg>
                    </button>
                    <button className="text-brand-black">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z" />
                      </svg>
                    </button>
                    <button className="text-brand-black">
                      <Share2 className="w-6 h-6" />
                    </button>
                  </div>
                  <div className="mt-2">
                    <p className="font-semibold text-[15px] text-brand-black">127 Me gusta</p>
                  </div>
                  <div className="mt-1">
                    {isEditingDescription ? (
                      <div className="space-y-2">
                        <textarea className="w-full p-2 text-[15px] text-brand-black border border-neutral-200 rounded-md focus:ring-2 focus:ring-brand-primary focus:border-brand-primary min-h-[100px]" value={editedDescription} onChange={(e) => setEditedDescription(e.target.value)} placeholder="Escribe la descripción aquí..." />
                        <div className="flex space-x-2">
                          <button onClick={() => { setPostDescription(editedDescription); setIsEditingDescription(false); }} className="h-9 px-4 inline-flex items-center justify-center bg-brand-primary text-white text-[15px] rounded-md font-medium shadow-sm hover:bg-brand-primary-hover">Guardar</button>
                          <button onClick={() => { setEditedDescription(postDescription); setIsEditingDescription(false); }} className="h-9 px-4 inline-flex items-center justify-center bg-neutral-200 text-neutral-600 text-[15px] rounded-md font-medium shadow-sm hover:bg-neutral-300">Cancelar</button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-[15px] text-brand-black"><span className="font-semibold mr-1">{instagramUsername}</span>{postDescription}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="w-full max-w-sm mx-auto md:mx-0 md:w-60 md:flex-shrink-0 md:flex md:flex-col md:sticky md:top-1/2 md:-translate-y-1/2">
                <button onClick={() => { if (!isInstagramConnected) { setShowConnectPrompt(true); return; } handlePostToInstagram(); }} className={`${!isInstagramConnected ? 'bg-neutral-200 text-neutral-500' : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'} w-full mb-2 h-10 rounded-md font-medium transition-colors flex items-center justify-center gap-2 px-3 shadow-sm text-[15px]`} disabled={isGeneratingDescription || isPostingToInstagram} aria-disabled={!isInstagramConnected} title={!isInstagramConnected ? 'Conectá tu cuenta de Instagram para publicar' : ''}>
                  {isPostingToInstagram ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-primary mr-2"></div>
                      <span>Publicando...</span>
                    </>
                  ) : instagramPostSuccess ? (
                    <>
                      <Check className="w-4 h-4 flex-shrink-0" />
                      <span>Publicado con éxito</span>
                    </>
                  ) : !isInstagramConnected ? (
                    <>
                      <Lock className="w-4 h-4 flex-shrink-0" />
                      <span>Publicar en Instagram</span>
                    </>
                  ) : (
                    <>
                      <Instagram className="w-4 h-4 flex-shrink-0" />
                      <span>Publicar en Instagram</span>
                    </>
                  )}
                </button>
                <button onClick={() => { setEditedDescription(postDescription); setIsEditingDescription(true); }} className="w-full mb-2 h-10 bg-brand-primary text-white rounded-md font-medium hover:bg-brand-primary-hover transition-colors flex items-center justify-center gap-2 px-3 shadow-sm text-[15px]">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 flex-shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
                  </svg>
                  <span>Editar Descripción</span>
                </button>
                <button onClick={() => saveImages([currentViewImage!])} className="w-full mb-2 h-10 bg-surface-card text-neutral-600 border border-neutral-200 rounded-md font-medium hover:bg-surface-elevated transition-colors flex items-center justify-center gap-2 px-3 shadow-sm text-[15px]">
                  <Download className="w-4 h-4 flex-shrink-0" />
                  <span>Descargar</span>
                </button>
                <button onClick={copyDescription} className="w-full h-10 bg-surface-card text-neutral-600 border border-neutral-200 rounded-md font-medium hover:bg-surface-elevated transition-colors flex items-center justify-center gap-2 px-3 shadow-sm text-[15px]">
                  <Copy className="w-4 h-4 flex-shrink-0" />
                  <span>Copiar Descripción</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        {connectPrompt}
      </>
    );
  }

  const currentIsVideo = currentViewImage?.isVideo === true;

  // Main inline view
  return (
    <div className={`pt-2 md:pt-0 pb-0 w-full bg-surface-card sm:bg-transparent min-h-[100dvh] md:min-h-0 ${isStandalonePage ? 'px-4 md:px-0' : ''}`}>
      {/* Desktop sticky header — Apple frosted glass nav bar */}
      <div className="hidden md:block fixed top-0 right-0 z-40" style={{ left: sidebarWidth }}>
        <div className="bg-white border-b border-black/[0.06]">
          <div className="max-w-6xl mx-auto px-8">
            <div className="flex items-center justify-between h-14">
              <div className="flex items-center gap-2">
                {!isOnboarding && (
                  <button
                    type="button"
                    onClick={() => {
                      if (onReturnToConfig) onReturnToConfig();
                      else if (onClose) onClose();
                      else navigate(-1);
                    }}
                    className="inline-flex items-center gap-1 text-[#1644EB] hover:opacity-70 transition-opacity text-[15px] font-normal"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    <span>Atrás</span>
                  </button>
                )}
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                <h1 className="text-[17px] font-semibold text-brand-black/90">Imágenes Generadas</h1>
                <button
                  className="inline-flex items-center justify-center cursor-pointer text-neutral-400 hover:text-neutral-600 transition-colors rounded-md p-1"
                  onClick={() => setShowInfoTooltip(true)}
                  aria-label="Mostrar información"
                >
                  <Info size={16} />
                </button>
              </div>
              <button
                type="button"
                onClick={() => { mergeLastCrearAnuncioState({ selectedVariantIds: [] }); navigate('/redes-sociales/crear-anuncio/seleccionar-productos'); }}
                className="inline-flex items-center justify-center h-[36px] px-4 rounded-[10px] bg-brand-primary text-white text-[15px] font-medium shadow-sm active:brightness-95 transition-all"
              >
                Seguir generando
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop layout: matches EditarImagen sizing — image drives its own width */}
      <div className="hidden md:flex md:flex-row md:items-start md:justify-center fixed top-[58px] bottom-0 right-0 z-30 bg-surface-bg px-4 pt-4 gap-4" style={{ left: sidebarWidth }}>
        {/* Left column: image viewer or crop canvas */}
        {showCropModal && currentViewImage && !currentIsVideo && isDesktop ? (
          <div className="relative rounded-lg overflow-hidden" style={{ height: 'calc(100vh - 8rem)', width: 'calc(100% - 300px - 1rem)' }}>
            <div className="absolute inset-0">
              <ImageCropCanvas cropState={cropState} />
            </div>
          </div>
        ) : (
        <div className={`flex-shrink-0 flex items-center justify-center relative transition-opacity duration-700 ease-out ${animationPhase === 'initial' ? 'opacity-0' : 'opacity-100'}`} style={{ height: 'calc(100vh - 8rem)' }}>
          {showFavoriteMessage && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-card/90 backdrop-blur-sm border border-neutral-200 rounded-full shadow-sm">
                <Heart className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" />
                <span className="text-[13px] font-medium text-neutral-700 whitespace-nowrap">Imagen marcada como favorita</span>
              </div>
            </div>
          )}
          {currentViewImage ? (
            currentIsVideo ? (
              <video
                key={currentViewImage.id}
                src={currentViewImage.signedUrl}
                controls
                playsInline
                style={{ height: 'calc(100vh - 8rem)', width: 'auto', maxWidth: 'calc(100vw - 500px)' }}
                className="object-contain rounded-lg"
                onLoadedData={() => setMainImageLoaded(true)}
              />
            ) : (
              <img
                src={currentViewImage.signedUrl}
                alt={`Generated image ${currentViewImage.id}`}
                style={{ height: 'calc(100vh - 8rem)', width: 'auto', maxWidth: 'calc(100vw - 500px)' }}
                className="object-contain rounded-lg"
                onLoad={() => setMainImageLoaded(true)}
              />
            )
          ) : (
            <div className="text-neutral-500 text-[15px]">Selecciona una imagen</div>
          )}
        </div>
        )}

        {/* Right column: crop controls OR scrollable image list + action buttons */}
        {showCropModal && currentViewImage && !currentIsVideo && isDesktop ? (
          <div className="w-[300px] flex-shrink-0 bg-surface-card rounded-lg border border-neutral-200 overflow-hidden" style={{ height: 'calc(100vh - 8rem)' }}>
            <ImageCropControls cropState={cropState} onCancel={() => setShowCropModal(false)} variant="desktop" />
          </div>
        ) : (
        <div className={`flex-1 min-w-[33%] max-w-[50%] flex flex-col overflow-hidden transition-opacity duration-700 ease-out ${animationPhase === 'initial' ? 'opacity-0' : 'opacity-100'}`} style={{ height: 'calc(100vh - 8rem)' }}>
          {error && (
            <div className="mb-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-sm text-[13px]">{error}</div>
          )}
          {/* Scrollable image grid */}
          <div className="flex-1 overflow-y-auto min-h-0 px-1" onScroll={(e) => {
            // Infinite scroll for history
            if (!hasMoreHistory || loadingMoreHistory || loadingHistory) return;
            const el = e.currentTarget;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
              fetchHistoryImages(historyImages.length);
            }
          }}>
            {images.length > 0 ? (
              <>
                {/* Section: Últimas generadas */}
                <div className="pt-1 pb-2">
                  <h2 className="text-[13px] font-semibold text-brand-black/50 uppercase tracking-wide">Últimas generadas</h2>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {/* Generating placeholder cards */}
                  {generatingPlaceholderCount > 0 && Array.from({ length: generatingPlaceholderCount }).map((_, i) => (
                    <div key={`desktop-gen-placeholder-${i}`} className="relative rounded-lg overflow-hidden">
                      <div className="aspect-[4/5] relative bg-neutral-100 animate-pulse">
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5">
                          <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center">
                            <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
                          </div>
                          <span className="text-[13px] font-medium text-neutral-400">Generando...</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {images.map((image, index) => (
                    <div
                      key={`desktop-card-${image.id}`}
                      className={`group relative rounded-lg overflow-hidden cursor-pointer transition-all duration-500 ease-out ${currentViewImage?.id === image.id ? 'ring-2 ring-brand-primary ring-offset-2' : ''} ${animationPhase === 'initial' ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
                      style={{ transitionDelay: animationPhase !== 'initial' ? `${index * 60}ms` : '0ms' }}
                      onClick={() => {
                        setCurrentViewImage(image);
                        setSelectedImageIds([image.id]);
                        if (image.optSignedUrl && image.signedUrl === image.optSignedUrl) {
                          const fullPath = `${companyId}/full/${image.fileName}`;
                          const cachedFull = getCachedSignedUrl(fullPath);
                          if (cachedFull) {
                            setCurrentViewImage(prev => prev && prev.id === image.id ? { ...prev, signedUrl: cachedFull } : prev);
                            setImages(prev => prev.map(img => img.id === image.id ? { ...img, signedUrl: cachedFull } : img));
                            setHistoryImages(prev => prev.map(img => img.id === image.id ? { ...img, signedUrl: cachedFull } : img));
                          } else {
                            supabase.storage.from('generadas').createSignedUrl(fullPath, 3600).then(({ data }) => {
                              if (data?.signedUrl) {
                                const fullUrl = data.signedUrl;
                                setCachedSignedUrl(fullPath, fullUrl);
                                setCurrentViewImage(prev => prev && prev.id === image.id ? { ...prev, signedUrl: fullUrl } : prev);
                                setImages(prev => prev.map(img => img.id === image.id ? { ...img, signedUrl: fullUrl } : img));
                                setHistoryImages(prev => prev.map(img => img.id === image.id ? { ...img, signedUrl: fullUrl } : img));
                              }
                            });
                          }
                        }
                      }}
                    >
                      <div className="aspect-[4/5] relative">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleFavoriteImage(image); }}
                          className="absolute top-2 right-2 z-20 w-7 h-7 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center transition-colors hover:bg-white"
                          aria-label={image.favorito ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                        >
                          <Heart className={`w-3.5 h-3.5 ${image.favorito ? 'text-red-500 fill-current' : 'text-neutral-500'}`} />
                        </button>
                        {image.isVideo ? (
                          <div className="absolute inset-0 flex items-center justify-center bg-neutral-100">
                            {image.optSignedUrl && (
                              <img src={image.optSignedUrl} alt="Video thumbnail" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                            )}
                            <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center z-10">
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-6 h-6"><path d="M8 5.14v14l11-7-11-7z"/></svg>
                            </div>
                          </div>
                        ) : (
                          <img
                            src={image.optSignedUrl || image.signedUrl}
                            alt={`Generated image ${image.id}`}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            loading="lazy"
                            onLoad={() => setLoadedThumbnails(prev => new Set(prev).add(image.id))}
                          />
                        )}
                        {/* Gradient overlay with text */}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pt-10 pb-3 px-3 pointer-events-none">
                          <p className="text-[13px] font-medium text-white truncate">{image.isVideo ? 'Video' : (image.variante_nombre || 'Imagen')}</p>
                          {image.created_at && (
                            <p className="text-[11px] text-white/60 mt-0.5">{formatSmartDate(image.created_at)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Section: Recientes (history) */}
                {historyImages.length > 0 && (
                  <>
                    <div className="pt-5 pb-2">
                      <h2 className="text-[13px] font-semibold text-brand-black/50 uppercase tracking-wide">Recientes</h2>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {historyImages.map((image) => (
                        <div
                          key={`desktop-history-${image.id}`}
                          className={`group relative rounded-lg overflow-hidden cursor-pointer ${currentViewImage?.id === image.id ? 'ring-2 ring-brand-primary ring-offset-2' : ''}`}
                          onClick={() => {
                            setCurrentViewImage(image);
                            setSelectedImageIds([image.id]);
                            if (image.optSignedUrl && image.signedUrl === image.optSignedUrl) {
                              const fullPath = `${companyId}/full/${image.fileName}`;
                              const cachedFull = getCachedSignedUrl(fullPath);
                              if (cachedFull) {
                                setCurrentViewImage(prev => prev && prev.id === image.id ? { ...prev, signedUrl: cachedFull } : prev);
                                setImages(prev => prev.map(img => img.id === image.id ? { ...img, signedUrl: cachedFull } : img));
                                setHistoryImages(prev => prev.map(img => img.id === image.id ? { ...img, signedUrl: cachedFull } : img));
                              } else {
                                supabase.storage.from('generadas').createSignedUrl(fullPath, 3600).then(({ data }) => {
                                  if (data?.signedUrl) {
                                    const fullUrl = data.signedUrl;
                                    setCachedSignedUrl(fullPath, fullUrl);
                                    setCurrentViewImage(prev => prev && prev.id === image.id ? { ...prev, signedUrl: fullUrl } : prev);
                                    setImages(prev => prev.map(img => img.id === image.id ? { ...img, signedUrl: fullUrl } : img));
                                    setHistoryImages(prev => prev.map(img => img.id === image.id ? { ...img, signedUrl: fullUrl } : img));
                                  }
                                });
                              }
                            }
                          }}
                        >
                          <div className="aspect-[4/5] relative">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleFavoriteImage(image); }}
                              className="absolute top-2 right-2 z-20 w-7 h-7 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center transition-colors hover:bg-white"
                              aria-label={image.favorito ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                            >
                              <Heart className={`w-3.5 h-3.5 ${image.favorito ? 'text-red-500 fill-current' : 'text-neutral-500'}`} />
                            </button>
                            {image.isVideo ? (
                              <div className="absolute inset-0 flex items-center justify-center bg-neutral-100">
                                {image.optSignedUrl && (
                                  <img src={image.optSignedUrl} alt="Video thumbnail" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                                )}
                                <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center z-10">
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-6 h-6"><path d="M8 5.14v14l11-7-11-7z"/></svg>
                                </div>
                              </div>
                            ) : (
                              <img
                                src={image.optSignedUrl || image.signedUrl}
                                alt={`History image ${image.id}`}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                loading="lazy"
                              />
                            )}
                            {/* Gradient overlay with text */}
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pt-10 pb-3 px-3 pointer-events-none">
                              <p className="text-[13px] font-medium text-white truncate">{image.isVideo ? 'Video' : (image.variante_nombre || 'Imagen')}</p>
                              {image.created_at && (
                                <p className="text-[11px] text-white/60 mt-0.5">{formatSmartDate(image.created_at)}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Loading history skeleton */}
                {loadingHistory && (
                  <div className="pt-5">
                    <div className="pb-2">
                      <h2 className="text-[13px] font-semibold text-brand-black/50 uppercase tracking-wide">Recientes</h2>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={`desktop-skel-${i}`} className="rounded-lg overflow-hidden">
                          <div className="aspect-[4/5] bg-neutral-200 animate-pulse" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Loading more indicator */}
                {loadingMoreHistory && (
                  <div className="flex items-center justify-center py-6">
                    <div className="w-6 h-6 border-2 border-neutral-300 border-t-brand-primary rounded-full animate-spin" />
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center py-16">
                <p className="text-neutral-500 text-[15px]">No se han generado imágenes</p>
              </div>
            )}
          </div>

          {/* Action buttons — below the scrollable area */}
          <div className={`flex-shrink-0 pt-3 pb-2 transition-opacity duration-300 ease-out ${animationPhase === 'initial' || animationPhase === 'phase1' ? 'opacity-0' : 'opacity-100'}`}>
            <div className="relative">
              {/* More options overlay (desktop) */}
              {showMoreOptions && (
                <>
                  <div className="fixed inset-0 z-[5]" onClick={() => setShowMoreOptions(false)} />
                  <div className="absolute bottom-full left-0 right-0 mb-2 bg-surface-card rounded-lg border border-neutral-200 shadow-lg p-2 z-10">
                    <button
                      type="button"
                      onClick={() => handleRegenerateInline()}
                      disabled={isGeneratingMore}
                      className="flex items-center gap-3 w-full p-3 rounded-md hover:bg-neutral-100 transition-colors text-left"
                    >
                      <div className="w-9 h-9 rounded-sm bg-neutral-100 flex items-center justify-center flex-shrink-0">
                        {isGeneratingMore ? (
                          <Loader2 className="w-4 h-4 text-neutral-600 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4 text-neutral-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-medium text-brand-black">Reintentar</p>
                        <p className="text-[13px] text-neutral-500 mt-0.5">Generá nuevas imágenes con la misma configuración</p>
                      </div>
                    </button>
                    {!currentIsVideo && currentViewImage && (
                      <button
                        type="button"
                        onClick={() => { setShowMoreOptions(false); setShowCropModal(true); }}
                        className="flex items-center gap-3 w-full p-3 rounded-md hover:bg-neutral-100 transition-colors text-left"
                      >
                        <div className="w-9 h-9 rounded-sm bg-neutral-100 flex items-center justify-center flex-shrink-0">
                          <Crop className="w-4 h-4 text-neutral-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[15px] font-medium text-brand-black">Recortar</p>
                          <p className="text-[13px] text-neutral-500 mt-0.5">Recortá la imagen al tamaño que necesites</p>
                        </div>
                      </button>
                    )}
                  </div>
                </>
              )}
              {/* Action buttons */}
              {isOnboarding ? (
                <button
                  onClick={handleSaveSelected}
                  disabled={selectedImageIds.length === 0}
                  className={`w-full flex items-center justify-center gap-2 h-12 rounded-md font-medium transition-colors text-[15px] ${selectedImageIds.length > 0 ? 'bg-brand-primary text-white hover:bg-brand-primary-hover shadow-sm' : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'}`}
                >
                  <Download className="w-5 h-5" />
                  <span>Descargar</span>
                </button>
              ) : (
              <div className="grid grid-cols-3 gap-2">
                {/* Descargar — primary blue */}
                <button
                  onClick={handleSaveSelected}
                  disabled={selectedImageIds.length === 0}
                  className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${selectedImageIds.length > 0 ? 'bg-brand-primary text-white hover:bg-brand-primary-hover shadow-sm' : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'}`}
                >
                  <Download className="w-5 h-5" />
                  <span>Descargar</span>
                </button>
                {/* Editar */}
                {!currentIsVideo && (
                  <button
                    onClick={handleEditSelected}
                    disabled={!currentViewImage && selectedImageIds.length === 0}
                    className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${(!currentViewImage && selectedImageIds.length === 0) ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed' : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'}`}
                  >
                    <Pencil className="w-5 h-5" />
                    <span>Editar</span>
                  </button>
                )}
                {/* Favorito */}
                <button
                  onClick={handleFavoriteCurrent}
                  disabled={isFavoriting || (!currentViewImage && selectedImageIds.length === 0)}
                  aria-pressed={(currentViewImage?.favorito === true) || (!currentViewImage && selectedImageIds.length > 0 && (images.find((img) => img.id === selectedImageIds[0])?.favorito === true))}
                  className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${isFavoriting ? 'opacity-50 cursor-not-allowed' : ''} ${((currentViewImage?.favorito === true) || (!currentViewImage && selectedImageIds.length > 0 && (images.find((img) => img.id === selectedImageIds[0])?.favorito === true))) ? 'bg-brand-primary text-white hover:bg-brand-primary-hover' : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'}`}
                  aria-label="Favorito"
                >
                  {isFavoriting ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-primary" />
                  ) : (
                    <Heart
                      className="w-5 h-5"
                      fill={((currentViewImage?.favorito === true) || (!currentViewImage && selectedImageIds.length > 0 && (images.find((img) => img.id === selectedImageIds[0])?.favorito === true))) ? 'currentColor' : 'none'}
                    />
                  )}
                  <span>Favorito</span>
                </button>
                {/* Animar (generar video) */}
                {!currentIsVideo && (
                  <button
                    onClick={openVideoMenu}
                    disabled={!currentViewImage}
                    className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${!currentViewImage ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed' : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'}`}
                  >
                    <Video className="w-5 h-5" />
                    <span>Animar</span>
                  </button>
                )}
                {/* Carrusel (generar más como esta imagen) */}
                {!currentIsVideo && (
                  <button
                    onClick={handleCarruselClick}
                    disabled={!currentViewImage || isGeneratingNewAngles}
                    className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${(!currentViewImage || isGeneratingNewAngles) ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed' : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'}`}
                  >
                    {isGeneratingNewAngles ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Copy className="w-5 h-5" />
                    )}
                    <span>Carrusel</span>
                  </button>
                )}
                {/* Más */}
                <button
                  type="button"
                  onClick={() => setShowMoreOptions(!showMoreOptions)}
                  className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${showMoreOptions ? 'bg-brand-primary text-white hover:bg-brand-primary-hover' : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'}`}
                >
                  <MoreHorizontal className="w-5 h-5" />
                  <span>Más</span>
                </button>
              </div>
              )}
              {/* Description panel (desktop) */}
              {showDescriptionPanel && currentViewImage && (
                <div className="absolute bottom-full left-0 right-0 mb-2 z-20">
                  <div className="relative bg-surface-card rounded-lg shadow-lg border border-neutral-200 p-4">
                    <button
                      onClick={() => setShowDescriptionPanel(false)}
                      className="absolute top-3 right-3 w-8 h-8 rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 inline-flex items-center justify-center"
                      aria-label="Cerrar"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    {isGeneratingDescription ? (
                      <div className="flex items-center gap-2 text-neutral-600">
                        <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
                        <span className="text-[15px]">Generando descripción...</span>
                        <div className="absolute left-3 right-3 bottom-0 h-1.5 rounded-full overflow-hidden bg-neutral-200">
                          <div
                            className="h-full rounded-full"
                            style={{ width: descriptionProgressActive ? '100%' : '0%', transition: 'width 20s cubic-bezier(0, 0, 0.2, 1)', background: 'linear-gradient(to right, #D1D5DB 10%, var(--brand-primary) 70%)' }}
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <h3 className="text-[17px] font-semibold text-brand-black mb-2">Descripción para post</h3>
                        <div className="text-[15px] text-neutral-600 whitespace-pre-wrap pr-6">{generatedDescription}</div>
                        <div className="mt-3 flex justify-end">
                          <button
                            onClick={copyGeneratedDescription}
                            className={`${copiedDescription ? 'bg-green-600 hover:bg-green-700' : 'bg-brand-primary hover:bg-brand-primary-hover'} h-9 px-4 text-white rounded-md shadow-sm text-[15px] font-medium flex items-center gap-2`}
                          >
                            {copiedDescription ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            {copiedDescription ? 'Copiado' : 'Copiar'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Mobile layout — fixed full-screen overlay so no parent padding leaks through */}
      <div ref={mobileScrollRef} className="md:hidden fixed inset-0 z-40 bg-[#F2F2F7] overflow-y-auto" style={{ paddingTop: '3.5rem' }} onScroll={handleGridScroll}>
        {error && (
          <div className="mx-4 mt-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-sm text-[13px]">{error}</div>
        )}

        {/* ==================== GRID VIEW (mobile) ==================== */}
        {mobileView === 'grid' && (
          <div className="flex flex-col min-h-full bg-[#F2F2F7] pt-1 px-4 pb-4">
            {images.length > 0 ? (
              <>
                {/* Seguir generando button */}
                <div className="pt-2 pb-1">
                  <button
                    type="button"
                    onClick={() => { mergeLastCrearAnuncioState({ selectedVariantIds: [] }); navigate('/redes-sociales/crear-anuncio/seleccionar-productos'); }}
                    className="w-full h-[44px] inline-flex items-center justify-center rounded-[12px] text-[15px] font-bold text-brand-primary bg-white shadow-sm active:brightness-95 transition-all"
                  >
                    Seguir generando
                  </button>
                </div>
                {/* Section: Últimas generadas */}
                <div className="pt-3 pb-2">
                  <h2 className="text-[13px] font-semibold text-brand-black/50 uppercase tracking-wide">Últimas generadas</h2>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {/* Generating placeholder cards */}
                  {generatingPlaceholderCount > 0 && Array.from({ length: generatingPlaceholderCount }).map((_, i) => (
                    <div key={`generating-placeholder-${i}`} className="relative rounded-lg overflow-hidden">
                      <div className="aspect-[4/5] relative bg-neutral-100 animate-pulse">
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5">
                          <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center">
                            <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
                          </div>
                          <span className="text-[13px] font-medium text-neutral-400">Generando...</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {images.map((image, index) => (
                    <div
                      key={`grid-current-${image.id}`}
                      data-image-id={image.id}
                      className={`group relative rounded-lg overflow-hidden cursor-pointer transition-all duration-500 ease-out ${animationPhase === 'initial' ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
                      style={{ transitionDelay: animationPhase !== 'initial' ? `${index * 60}ms` : '0ms' }}
                      onClick={(e) => handleGridImageTap(image, e)}
                    >
                      <div className="aspect-[4/5] relative">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleFavoriteImage(image); }}
                          className="absolute top-2 right-2 z-20 w-7 h-7 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center transition-colors hover:bg-white"
                          aria-label={image.favorito ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                        >
                          <Heart className={`w-3.5 h-3.5 ${image.favorito ? 'text-red-500 fill-current' : 'text-neutral-500'}`} />
                        </button>
                        {image.isVideo ? (
                          <div className="absolute inset-0 flex items-center justify-center bg-neutral-100">
                            {image.optSignedUrl && (
                              <img src={image.optSignedUrl} alt="Video thumbnail" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                            )}
                            <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center z-10">
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-6 h-6"><path d="M8 5.14v14l11-7-11-7z"/></svg>
                            </div>
                          </div>
                        ) : (
                          <img
                            src={image.optSignedUrl || image.signedUrl}
                            alt={`Generated image ${image.id}`}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            loading="lazy"
                            onLoad={() => setLoadedThumbnails(prev => new Set(prev).add(image.id))}
                          />
                        )}
                        {/* Gradient overlay with text — matches history page */}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pt-10 pb-3 px-3 pointer-events-none">
                          <p className="text-[13px] font-medium text-white truncate">{image.isVideo ? 'Video' : (image.variante_nombre || 'Imagen')}</p>
                          {image.created_at && (
                            <p className="text-[11px] text-white/60 mt-0.5">{formatSmartDate(image.created_at)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Section: Recientes */}
                {historyImages.length > 0 && (
                  <>
                    <div className="pt-5 pb-2">
                      <h2 className="text-[13px] font-semibold text-brand-black/50 uppercase tracking-wide">Recientes</h2>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {historyImages.map((image) => (
                        <div
                          key={`grid-history-${image.id}`}
                          data-image-id={image.id}
                          className="group relative rounded-lg overflow-hidden cursor-pointer"
                          onClick={(e) => handleGridImageTap(image, e)}
                        >
                          <div className="aspect-[4/5] relative">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleFavoriteImage(image); }}
                              className="absolute top-2 right-2 z-20 w-7 h-7 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center transition-colors hover:bg-white"
                              aria-label={image.favorito ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                            >
                              <Heart className={`w-3.5 h-3.5 ${image.favorito ? 'text-red-500 fill-current' : 'text-neutral-500'}`} />
                            </button>
                            {image.isVideo ? (
                              <div className="absolute inset-0 flex items-center justify-center bg-neutral-100">
                                {image.optSignedUrl && (
                                  <img src={image.optSignedUrl} alt="Video thumbnail" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                                )}
                                <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center z-10">
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-6 h-6"><path d="M8 5.14v14l11-7-11-7z"/></svg>
                                </div>
                              </div>
                            ) : (
                              <img
                                src={image.optSignedUrl || image.signedUrl}
                                alt={`History image ${image.id}`}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                loading="lazy"
                              />
                            )}
                            {/* Gradient overlay with text — matches history page */}
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pt-10 pb-3 px-3 pointer-events-none">
                              <p className="text-[13px] font-medium text-white truncate">{image.isVideo ? 'Video' : (image.variante_nombre || 'Imagen')}</p>
                              {image.created_at && (
                                <p className="text-[11px] text-white/60 mt-0.5">{formatSmartDate(image.created_at)}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Loading history skeleton (initial load) */}
                {loadingHistory && (
                  <div className="pt-5">
                    <div className="pb-2">
                      <h2 className="text-[13px] font-semibold text-brand-black/50 uppercase tracking-wide">Recientes</h2>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div key={`skel-${i}`} className="rounded-lg overflow-hidden">
                          <div className="aspect-[4/5] bg-neutral-200 animate-pulse" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Loading more indicator (infinite scroll) */}
                {loadingMoreHistory && (
                  <div className="flex items-center justify-center py-6">
                    <div className="w-6 h-6 border-2 border-neutral-300 border-t-brand-primary rounded-full animate-spin" />
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-surface-card py-16">
                <p className="text-neutral-500 text-[15px]">No se han generado imágenes</p>
              </div>
            )}
          </div>
        )}

        {/* ==================== DETAIL VIEW (mobile) ==================== */}
        {mobileView === 'detail' && (
          <div className="flex flex-col" style={{ height: 'calc(100dvh - 3.5rem)' }}>
            {/* Image viewer or crop canvas — fills all available space above buttons */}
            {showCropModal && currentViewImage && !currentIsVideo && !isDesktop ? (
              <div className="flex-1 min-h-0 relative">
                <div className="absolute inset-0">
                  <ImageCropCanvas cropState={cropState} />
                </div>
              </div>
            ) : (
            <div ref={heroImageRef} className="flex-1 min-h-0 relative bg-[#F2F2F7]">
              {showFavoriteMessage && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-card/90 backdrop-blur-sm border border-neutral-200 rounded-full shadow-sm">
                    <Heart className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" />
                    <span className="text-[13px] font-medium text-neutral-700 whitespace-nowrap">Imagen marcada como favorita</span>
                  </div>
                </div>
              )}
              {allGridImages.length > 0 ? (
                <div
                  ref={scrollContainerRef}
                  onScroll={handleDetailScroll}
                  className="w-full h-full flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch', opacity: (heroAnimating && heroDirection === 'forward') ? 0 : 1 }}
                >
                  {allGridImages.map((image) => {
                    const isVideo = image.isVideo || image.fileName?.endsWith('.mp4');
                    return (
                      <div
                        key={`detail-snap-${image.id}`}
                        className="w-full h-full flex-shrink-0 snap-center flex items-center justify-center p-3"
                      >
                        {isVideo ? (
                          <video
                            src={image.signedUrl}
                            controls
                            playsInline
                            className="max-w-full max-h-full object-contain rounded-2xl"
                            onLoadedData={() => setMainImageLoaded(true)}
                          />
                        ) : (
                          <img
                            src={image.signedUrl}
                            alt={`Generated image ${image.id}`}
                            className="max-w-full max-h-full object-contain rounded-2xl"
                            onLoad={() => setMainImageLoaded(true)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-neutral-500">
                  <p className="text-[15px]">Selecciona una imagen para visualizarla</p>
                </div>
              )}
            </div>
            )}

            {/* Action bar — static below image, same background */}
            <div className="flex-shrink-0 px-4 pt-2 bg-[#F2F2F7]" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}>
              {showCropModal && currentViewImage && !currentIsVideo && !isDesktop ? (
                <ImageCropControls cropState={cropState} onCancel={() => setShowCropModal(false)} variant="mobile" />
              ) : (
              <>
              <div className="relative">
                {/* More options overlay (mobile) */}
                {showMoreOptions && (
                  <>
                    <div className="fixed inset-0 z-[5]" onClick={() => setShowMoreOptions(false)} />
                    <div className="absolute bottom-full left-0 right-0 mb-2 bg-surface-card rounded-lg border border-neutral-200 shadow-lg p-2 z-10">
                      <button
                        type="button"
                        onClick={() => handleRegenerateInline()}
                        disabled={isGeneratingMore}
                        className="flex items-center gap-3 w-full p-3 rounded-md hover:bg-neutral-100 transition-colors text-left"
                      >
                        <div className="w-9 h-9 rounded-sm bg-neutral-100 flex items-center justify-center flex-shrink-0">
                          {isGeneratingMore ? (
                            <Loader2 className="w-4 h-4 text-neutral-600 animate-spin" />
                          ) : (
                            <Sparkles className="w-4 h-4 text-neutral-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[15px] font-medium text-brand-black">Reintentar</p>
                          <p className="text-[13px] text-neutral-500 mt-0.5">Generá nuevas imágenes con la misma configuración</p>
                        </div>
                      </button>
                      {!currentIsVideo && currentViewImage && (
                        <button
                          type="button"
                          onClick={() => { setShowMoreOptions(false); setShowCropModal(true); }}
                          className="flex items-center gap-3 w-full p-3 rounded-md hover:bg-neutral-100 transition-colors text-left"
                        >
                          <div className="w-9 h-9 rounded-sm bg-neutral-100 flex items-center justify-center flex-shrink-0">
                            <Crop className="w-4 h-4 text-neutral-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[15px] font-medium text-brand-black">Recortar</p>
                            <p className="text-[13px] text-neutral-500 mt-0.5">Recortá la imagen al tamaño que necesites</p>
                          </div>
                        </button>
                      )}
                    </div>
                  </>
                )}
                {/* Action buttons */}
                {isOnboarding ? (
                  <button
                    onClick={handleSaveSelected}
                    disabled={selectedImageIds.length === 0}
                    className={`w-full flex items-center justify-center gap-2 h-[var(--btn-h)] rounded-md font-medium transition-colors text-[15px] ${selectedImageIds.length > 0 ? 'bg-brand-primary text-white hover:bg-brand-primary-hover shadow-sm' : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'}`}
                  >
                    <Download className="w-5 h-5" />
                    <span>Descargar</span>
                  </button>
                ) : (
                <div className="grid grid-cols-3 gap-2">
                  {/* Descargar — primary blue */}
                  <button
                    onClick={handleSaveSelected}
                    disabled={selectedImageIds.length === 0}
                    className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${selectedImageIds.length > 0 ? 'bg-brand-primary text-white hover:bg-brand-primary-hover shadow-sm' : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'}`}
                  >
                    <Download className="w-5 h-5" />
                    <span>Descargar</span>
                  </button>
                  {/* Editar */}
                  {!currentIsVideo && (
                    <button
                      onClick={handleEditSelected}
                      disabled={!currentViewImage && selectedImageIds.length === 0}
                      className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${(!currentViewImage && selectedImageIds.length === 0) ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed' : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'}`}
                    >
                      <Pencil className="w-5 h-5" />
                      <span>Editar</span>
                    </button>
                  )}
                  {/* Favorito */}
                  <button
                    onClick={handleFavoriteCurrent}
                    disabled={isFavoriting || (!currentViewImage && selectedImageIds.length === 0)}
                    aria-pressed={(currentViewImage?.favorito === true) || (!currentViewImage && selectedImageIds.length > 0 && (images.find((img) => img.id === selectedImageIds[0])?.favorito === true))}
                    className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${isFavoriting ? 'opacity-50 cursor-not-allowed' : ''} ${((currentViewImage?.favorito === true) || (!currentViewImage && selectedImageIds.length > 0 && (images.find((img) => img.id === selectedImageIds[0])?.favorito === true))) ? 'bg-brand-primary text-white hover:bg-brand-primary-hover' : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'}`}
                    aria-label="Favorito"
                  >
                    {isFavoriting ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-primary" />
                    ) : (
                      <Heart
                        className="w-5 h-5"
                        fill={((currentViewImage?.favorito === true) || (!currentViewImage && selectedImageIds.length > 0 && (images.find((img) => img.id === selectedImageIds[0])?.favorito === true))) ? 'currentColor' : 'none'}
                      />
                    )}
                    <span>Favorito</span>
                  </button>
                  {/* Animar (generar video) */}
                  {!currentIsVideo && (
                    <button
                      onClick={openVideoMenu}
                      disabled={!currentViewImage}
                      className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${!currentViewImage ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed' : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'}`}
                    >
                      <Video className="w-5 h-5" />
                      <span>Animar</span>
                    </button>
                  )}
                  {/* Carrusel (generar más como esta imagen) */}
                  {!currentIsVideo && (
                    <button
                      onClick={handleCarruselClick}
                      disabled={!currentViewImage || isGeneratingNewAngles}
                      className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${(!currentViewImage || isGeneratingNewAngles) ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed' : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'}`}
                    >
                      {isGeneratingNewAngles ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Copy className="w-5 h-5" />
                      )}
                      <span>Carrusel</span>
                    </button>
                  )}
                  {/* Más */}
                  <button
                    type="button"
                    onClick={() => setShowMoreOptions(!showMoreOptions)}
                    className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${showMoreOptions ? 'bg-brand-primary text-white hover:bg-brand-primary-hover' : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'}`}
                  >
                    <MoreHorizontal className="w-5 h-5" />
                    <span>Más</span>
                  </button>
                </div>
                )}
              </div>
              <div className="relative">
                {/* Description panel (mobile) */}
                {showDescriptionPanel && currentViewImage && (
                  <div className="absolute bottom-full left-0 right-0 mb-2 z-20">
                    <div className="relative bg-surface-card rounded-lg shadow-lg border border-neutral-200 p-4">
                      <button
                        onClick={() => setShowDescriptionPanel(false)}
                        className="absolute top-3 right-3 w-8 h-8 rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 inline-flex items-center justify-center"
                        aria-label="Cerrar"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      {isGeneratingDescription ? (
                        <div className="flex items-center gap-2 text-neutral-600">
                          <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
                          <span className="text-[15px]">Generando descripción...</span>
                          <div className="absolute left-3 right-3 bottom-0 h-1.5 rounded-full overflow-hidden bg-neutral-200">
                            <div
                              className="h-full rounded-full"
                              style={{ width: descriptionProgressActive ? '100%' : '0%', transition: 'width 20s cubic-bezier(0, 0, 0.2, 1)', background: 'linear-gradient(to right, #D1D5DB 10%, var(--brand-primary) 70%)' }}
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <h3 className="text-[17px] font-semibold text-brand-black mb-2">Descripción para post</h3>
                          <div className="text-[15px] text-neutral-600 whitespace-pre-wrap pr-6">{generatedDescription}</div>
                          <div className="mt-3 flex justify-end">
                            <button
                              onClick={copyGeneratedDescription}
                              className={`${copiedDescription ? 'bg-green-600 hover:bg-green-700' : 'bg-brand-primary hover:bg-brand-primary-hover'} h-9 px-4 text-white rounded-md shadow-sm text-[15px] font-medium flex items-center gap-2`}
                            >
                              {copiedDescription ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                              {copiedDescription ? 'Copiado' : 'Copiar'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
              </>
              )}
            </div>
          </div>
        )}

        {/* Hero animation overlay — Apple Photos style position transition (forward + reverse) */}
        {heroAnimating && heroRect && currentViewImage && (() => {
          const isEnd = heroPhase === 'end';
          const isReverse = heroDirection === 'reverse';
          const spring = 'cubic-bezier(0.2, 0.9, 0.3, 1)';
          const transition = `top 400ms ${spring}, left 400ms ${spring}, width 400ms ${spring}, height 400ms ${spring}`;

          let startTop: number, startLeft: number, startW: number, startH: number;
          let endTop: number, endLeft: number, endW: number, endH: number;

          if (isReverse) {
            // Reverse: start = viewer area (heroRect), end = thumbnail (heroTargetRect or fallback)
            startTop = heroRect.y;
            startLeft = heroRect.x;
            startW = heroRect.width;
            startH = heroRect.height;
            if (heroTargetRect) {
              endTop = heroTargetRect.y;
              endLeft = heroTargetRect.x;
              endW = heroTargetRect.width;
              endH = heroTargetRect.height;
            } else {
              // Fallback: shrink to center
              endTop = heroRect.y + heroRect.height * 0.3;
              endLeft = heroRect.x + heroRect.width * 0.3;
              endW = heroRect.width * 0.4;
              endH = heroRect.height * 0.4;
            }
          } else {
            // Forward: start = thumbnail (heroRect), end = contain-fit rect inside viewer area
            const viewerRect = heroImageRef.current?.getBoundingClientRect();
            const padding = 12;
            startTop = heroRect.y;
            startLeft = heroRect.x;
            startW = heroRect.width;
            startH = heroRect.height;
            const containerW = viewerRect ? viewerRect.width - padding * 2 : heroRect.width;
            const containerH = viewerRect ? viewerRect.height - padding * 2 : heroRect.height;
            const containerTop = viewerRect ? viewerRect.top + padding : heroRect.y;
            const containerLeft = viewerRect ? viewerRect.left + padding : heroRect.x;
            // Calculate contain-fit: image scaled to fit within container keeping aspect ratio
            const ns = heroNaturalSize.current;
            if (ns) {
              const imgAspect = ns.w / ns.h;
              const containerAspect = containerW / containerH;
              if (imgAspect > containerAspect) {
                // Image is wider — width fills, height is smaller
                endW = containerW;
                endH = containerW / imgAspect;
                endLeft = containerLeft;
                endTop = containerTop + (containerH - endH) / 2;
              } else {
                // Image is taller — height fills, width is smaller
                endH = containerH;
                endW = containerH * imgAspect;
                endTop = containerTop;
                endLeft = containerLeft + (containerW - endW) / 2;
              }
            } else {
              // No natural size available — fallback to full container
              endTop = containerTop;
              endLeft = containerLeft;
              endW = containerW;
              endH = containerH;
            }
          }

          return (
            <div
              className="pointer-events-none"
              style={{
                position: 'fixed',
                zIndex: 50,
                overflow: 'hidden',
                borderRadius: 16,
                top: isEnd ? endTop : startTop,
                left: isEnd ? endLeft : startLeft,
                width: isEnd ? endW : startW,
                height: isEnd ? endH : startH,
                transition: isEnd ? transition : 'none',
                willChange: 'top, left, width, height',
              }}
            >
              <img
                src={currentViewImage.signedUrl}
                alt="Hero transition"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            </div>
          );
        })()}
      </div>

      {/* Download Status Overlay */}
      {(isDownloading || downloadSuccess) && (
        <div className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4 sm:px-0">
          <div className="bg-surface-card rounded-lg shadow-lg px-6 py-10 max-w-sm w-full mx-4 sm:mx-auto flex flex-col items-center">
            {isDownloading && !downloadSuccess && (
              <>
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-primary mb-4"></div>
                <h3 className="text-[17px] font-semibold text-brand-black mb-1">Preparando descarga</h3>
              </>
            )}
            {downloadSuccess && (
              <>
                <div className="w-24 h-24 mb-2">
                  <DotLottieReact
                    src="/images/lottie_animations/Success.lottie"
                    autoplay
                    style={{ width: '100%', height: '100%' }}
                  />
                </div>
                <h3 className="text-[17px] font-semibold text-brand-black mb-1">Imagen descargada</h3>
              </>
            )}
          </div>
        </div>
      )}

      <SubscriptionLimitModal
        isOpen={showSubscriptionLimitModal}
        onClose={() => setShowSubscriptionLimitModal(false)}
        type={subscriptionLimitType}
      />

      {/* Insufficient images detailed modal */}
      {insufficientModal && (
        <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center">
          <div className="bg-surface-card rounded-lg shadow-md w-full max-w-xl mx-4">
            <div className="p-5">
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-3">
                Imágenes insuficientes para {insufficientModal.action}
              </h3>
              <p className="text-[15px] text-brand-black/90 mb-2">
                Para {insufficientModal.action} necesitás <strong>{insufficientModal.required} {insufficientModal.required === 1 ? 'imagen' : 'imágenes'}</strong>, pero {insufficientModal.remaining === 0 ? <>no te quedan imágenes restantes este mes</> : <>solo tenés <strong>{insufficientModal.remaining}</strong> restantes este mes</>}.
              </p>
              {insufficientModal.renewalDate && (
                <p className="text-[15px] text-neutral-500 mb-2">
                  Tu cupo de imágenes se renovará el {insufficientModal.renewalDate.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}.
                </p>
              )}
              <p className="text-[15px] text-brand-black/90 mb-6">
                Para seguir generando ya mismo, podés mejorar tu plan.
              </p>
              <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => { setInsufficientModal(null); navigate('/subscription'); }}
                  className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                >
                  Mejorar mi plan
                </button>
                <button
                  type="button"
                  onClick={() => setInsufficientModal(null)}
                  className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium rounded-md shadow-sm hover:bg-neutral-100 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CarruselConfirmModal
        isOpen={showCarruselConfirm}
        onConfirm={handleGenerateNewAngles}
        onCancel={() => setShowCarruselConfirm(false)}
        imageCount={2}
      />

      {showEditPlanRestriction && (
        <div className="fixed inset-0 flex items-center justify-center z-[100000]" style={{ position: 'fixed' }}>
          <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm" onClick={() => setShowEditPlanRestriction(false)} />
          <div className="relative bg-surface-card rounded-lg w-[95vw] max-w-md p-6 shadow-lg">
            <button
              onClick={() => setShowEditPlanRestriction(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 inline-flex items-center justify-center"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-[17px] font-semibold text-brand-black mb-2">Editar con IA disponible en Plan Pro o Ultra</h3>
            <p className="text-[15px] text-neutral-600 mb-5">La función de editar con IA está disponible para el plan Pro o Ultra. Mejorá tu plan para acceder a esta función y triplicar tu límite de fotos mensual.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button onClick={() => setShowEditPlanRestriction(false)} className="h-10 px-4 border border-neutral-200 rounded-md text-neutral-600 hover:bg-neutral-100 transition-colors shadow-sm text-[15px] font-medium">Cancelar</button>
              <button onClick={() => { setShowEditPlanRestriction(false); navigate('/subscription'); }} className="h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium shadow-sm transition-colors text-[15px]">Ver planes</button>
            </div>
          </div>
        </div>
      )}

      {showVideoPlanRestriction && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000] flex items-end sm:items-center sm:justify-center sm:p-4" style={{ position: 'fixed' }}>
          <div className="fixed inset-0" onClick={() => setShowVideoPlanRestriction(false)} />
          <div className="relative bg-surface-card w-full h-full sm:h-auto sm:max-w-2xl sm:rounded-lg shadow-md flex flex-col sm:flex-row sm:overflow-hidden">
            {/* Close button */}
            <button
              onClick={() => setShowVideoPlanRestriction(false)}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-sm text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 inline-flex items-center justify-center transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Title — mobile only */}
            <h3 className="sm:hidden text-[17px] font-semibold text-brand-black/90 text-center pt-16 pb-4 px-5">Función exclusiva</h3>

            {/* Video */}
            <div className="relative flex-shrink-0 px-5 sm:px-0 sm:w-1/2">
              <video
                src="/images/video_animar_imagen.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-full max-w-[280px] mx-auto sm:max-w-none aspect-[4/5] object-cover rounded-md sm:rounded-none sm:rounded-l-lg"
              />
              <div className="absolute -bottom-3 right-8 w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center shadow-md sm:hidden">
                <Lock className="w-5 h-5 text-white" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col justify-center p-5 pt-8 pb-8 sm:p-6">
              <h3 className="hidden sm:block text-[17px] font-semibold text-brand-black/90 text-center mb-3">Función exclusiva</h3>
              <p className="text-[15px] text-neutral-500 mb-5 sm:text-center">La función de generar video a partir de imagen está disponible para los planes <span className="font-semibold text-brand-black/80">Pro</span> y <span className="font-semibold text-brand-black/80">Ultra</span>.</p>
              <div className="flex flex-col gap-2 mt-auto pb-4 sm:mt-0 sm:pb-0">
                <button onClick={() => { setShowVideoPlanRestriction(false); navigate('/subscription'); }} className="inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors">Ver planes</button>
                <button onClick={() => setShowVideoPlanRestriction(false)} className="inline-flex items-center justify-center w-full h-10 bg-surface-card hover:bg-neutral-100 text-neutral-600 border border-neutral-200 rounded-md text-[15px] font-medium shadow-sm transition-colors">Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Tooltip Modal */}
      {showInfoTooltip && (
        <div className="fixed inset-0 flex items-center justify-center z-[99999]" style={{ position: 'fixed' }}>
          <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm" onClick={() => setShowInfoTooltip(false)} />
          <div className="relative bg-surface-card rounded-lg w-[95vw] max-w-md max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-start p-4 border-b border-neutral-200">
              <div className="w-8"></div>
              <h3 className="text-[17px] text-center font-semibold text-brand-black">Información de IA</h3>
              <button className="w-8 h-8 rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 inline-flex items-center justify-center" onClick={() => setShowInfoTooltip(false)} aria-label="Cerrar información">
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <AIInfoContent isFashion={isFashionRubro} />
            </div>
          </div>
        </div>
      )}

      {/* Removed local generating overlay; parent page overlay will remain visible */}

      {connectPrompt}

      {/* Loading animation for generating new angles — now handled by inline placeholder cards */}

      {/* Video generation menu */}
      {showVideoMenu && createPortal(
        <>
          {/* Backdrop */}
          <div
            className={`fixed inset-0 z-[200] bg-black/40 transition-opacity duration-300 ${videoMenuAnimated ? 'opacity-100' : 'opacity-0'}`}
            onClick={closeVideoMenu}
          />
          {/* Desktop: side panel (PruebaNuevaInterfaz style) */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[420px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[201] flex-col overflow-hidden transition-transform duration-300 ease-out ${videoMenuAnimated ? 'translate-x-0' : 'translate-x-[calc(100%+32px)]'}`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">
                {videoStep === 'prompt' ? 'Describir video' : 'Generar video'}
              </h3>
              <button type="button" onClick={closeVideoMenu} className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {videoStep === 'select' ? (
                <>
                  <video
                    src="/images/video_animar_imagen.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-3/5 mx-auto aspect-[4/5] object-cover rounded-[10px] mb-4"
                  />
                  <p className="text-[15px] text-neutral-400 mb-5 text-center">Convertí tu imagen en un video animado con movimiento realista generado por IA.</p>
                  <div className="space-y-1">
                    <button
                      type="button"
                      onClick={() => setVideoMode('automatico')}
                      className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-neutral-100 rounded-md ${videoMode === 'automatico' ? 'bg-neutral-100' : ''}`}
                    >
                      <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${videoMode === 'automatico' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-medium text-neutral-900">Automático</p>
                        <p className={`text-[13px] ${videoMode === 'automatico' ? 'text-neutral-600' : 'text-neutral-400'}`}>La IA decidirá el movimiento y estilo del video.</p>
                      </div>
                      {videoMode === 'automatico' && <Check className="w-4 h-4 text-brand-primary shrink-0 mr-1" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => setVideoMode('describir')}
                      className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-neutral-100 rounded-md ${videoMode === 'describir' ? 'bg-neutral-100' : ''}`}
                    >
                      <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${videoMode === 'describir' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                        <Pencil className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-medium text-neutral-900">Describir video</p>
                        <p className={`text-[13px] ${videoMode === 'describir' ? 'text-neutral-600' : 'text-neutral-400'}`}>Describí cómo querés que se mueva o qué pase.</p>
                      </div>
                      {videoMode === 'describir' && <Check className="w-4 h-4 text-brand-primary shrink-0 mr-1" />}
                    </button>
                  </div>
                  <div className="mt-4 flex items-center gap-2 px-1">
                    <Info className="w-3.5 h-3.5 text-info flex-shrink-0" />
                    <p className="text-[13px] text-neutral-500">Se consumirán {videoMode === 'describir' ? (videoDuration === '4' ? 12 : videoDuration === '8' ? 24 : 18) : 18} imágenes.</p>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-[15px] text-neutral-400 mb-4">Describí cómo querés que sea el video.</p>
                  <textarea
                    value={videoPrompt}
                    onChange={(e) => setVideoPrompt(e.target.value)}
                    placeholder="Ej: Que el modelo camine hacia la cámara con movimiento suave..."
                    className="w-full text-[15px] text-brand-black resize-none border border-neutral-200 rounded-md outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary p-4 bg-surface-card min-h-[120px]"
                  />
                  <div className="mt-5">
                    <label className="block text-[15px] font-medium text-brand-black/90 mb-2">Duración del video</label>
                    <div className="flex rounded-md border border-neutral-200 overflow-hidden">
                      {([['4', '4 seg'], ['6', '6 seg'], ['8', '8 seg']] as const).map(([val, label]) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setVideoDuration(val as '4' | '6' | '8')}
                          className={`flex-1 h-10 text-[15px] font-medium transition-colors ${
                            videoDuration === val
                              ? 'bg-neutral-100 text-neutral-900'
                              : 'bg-surface-card text-neutral-400 hover:bg-neutral-50'
                          } ${val !== '4' ? 'border-l border-neutral-200' : ''}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 px-1">
                    <Info className="w-3.5 h-3.5 text-info flex-shrink-0" />
                    <p className="text-[13px] text-neutral-500">Se consumirán {videoDuration === '4' ? 12 : videoDuration === '8' ? 24 : 18} imágenes.</p>
                  </div>
                </>
              )}
            </div>
            <div className="px-5 py-3 flex justify-end gap-3">
              <button type="button" onClick={closeVideoMenu} className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors">Cancelar</button>
              {videoStep === 'select' ? (
                <button
                  type="button"
                  onClick={() => {
                    if (videoMode === 'describir') {
                      setVideoStep('prompt');
                    } else {
                      handleGenerateVideo();
                    }
                  }}
                  className="text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors"
                >
                  {videoMode === 'describir' ? 'Siguiente' : 'Generar video'}
                </button>
              ) : videoPrompt.trim() ? (
                <button type="button" onClick={handleGenerateVideo} className="text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors">
                  Generar video
                </button>
              ) : (
                <button type="button" onClick={() => setVideoStep('select')} className="text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors">
                  Volver
                </button>
              )}
            </div>
          </div>
          {/* Mobile: full-height slide-up sheet (PruebaNuevaInterfaz style) */}
          <div
            className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px] flex flex-col"
            style={{
              height: 'calc(100dvh - 1rem)',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
              transform: videoMenuDragY > 0
                ? `translateY(${videoMenuDragY}px)`
                : videoMenuAnimated ? 'translateY(0)' : 'translateY(100%)',
              transition: videoMenuDragY === 0 ? 'transform 0.3s ease-out' : 'none'
            }}
          >
            {/* Drag handle */}
            <div
              className="py-2 cursor-grab active:cursor-grabbing"
              onTouchStart={(e) => { videoDragStartY.current = e.touches[0].clientY; }}
              onTouchMove={(e) => {
                if (videoDragStartY.current !== null) {
                  const dy = e.touches[0].clientY - videoDragStartY.current;
                  if (dy > 0) setVideoMenuDragY(dy);
                }
              }}
              onTouchEnd={() => {
                if (videoMenuDragY > 100) { closeVideoMenu(); }
                else { setVideoMenuDragY(0); }
                videoDragStartY.current = null;
              }}
            >
              <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
            </div>
            {/* Header: Cancelar | Title | Action */}
            <div className="flex items-center justify-between px-4 pb-3">
              <button type="button" onClick={closeVideoMenu} className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left">Cancelar</button>
              <h3 className="text-[15px] font-semibold text-brand-black/90">
                {videoStep === 'prompt' ? 'Describir video' : 'Generar video'}
              </h3>
              {videoStep === 'select' ? (
                <button
                  type="button"
                  onClick={() => {
                    if (videoMode === 'describir') {
                      setVideoStep('prompt');
                    } else {
                      handleGenerateVideo();
                    }
                  }}
                  className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right"
                >
                  {videoMode === 'describir' ? 'Siguiente' : 'Generar'}
                </button>
              ) : videoPrompt.trim() ? (
                <button type="button" onClick={handleGenerateVideo} className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right">
                  Generar
                </button>
              ) : (
                <button type="button" onClick={() => setVideoStep('select')} className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right">
                  Volver
                </button>
              )}
            </div>
            {/* Body */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {videoStep === 'select' ? (
                <>
                  <video
                    src="/images/video_animar_imagen.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-3/5 mx-auto aspect-[4/5] object-cover rounded-[10px] mb-4"
                  />
                  <p className="text-[15px] text-neutral-400 mb-5 text-center">Convertí tu imagen en un video animado con movimiento realista generado por IA.</p>
                  <div className="space-y-1">
                    <button
                      type="button"
                      onClick={() => setVideoMode('automatico')}
                      className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-neutral-100 rounded-md ${videoMode === 'automatico' ? 'bg-neutral-100' : ''}`}
                    >
                      <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${videoMode === 'automatico' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-medium text-neutral-900">Automático</p>
                        <p className={`text-[13px] ${videoMode === 'automatico' ? 'text-neutral-600' : 'text-neutral-400'}`}>La IA decidirá el movimiento y estilo.</p>
                      </div>
                      {videoMode === 'automatico' && <Check className="w-4 h-4 text-brand-primary shrink-0 mr-1" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => setVideoMode('describir')}
                      className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-neutral-100 rounded-md ${videoMode === 'describir' ? 'bg-neutral-100' : ''}`}
                    >
                      <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${videoMode === 'describir' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                        <Pencil className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-medium text-neutral-900">Describir video</p>
                        <p className={`text-[13px] ${videoMode === 'describir' ? 'text-neutral-600' : 'text-neutral-400'}`}>Describí cómo querés que se mueva o qué pase.</p>
                      </div>
                      {videoMode === 'describir' && <Check className="w-4 h-4 text-brand-primary shrink-0 mr-1" />}
                    </button>
                  </div>
                  <div className="mt-4 flex items-center gap-2 px-1">
                    <Info className="w-3.5 h-3.5 text-info flex-shrink-0" />
                    <p className="text-[13px] text-neutral-500">Se consumirán {videoMode === 'describir' ? (videoDuration === '4' ? 12 : videoDuration === '8' ? 24 : 18) : 18} imágenes.</p>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-[15px] text-neutral-400 mb-4">Describí cómo querés que sea el video.</p>
                  <textarea
                    value={videoPrompt}
                    onChange={(e) => setVideoPrompt(e.target.value)}
                    placeholder="Ej: Que el modelo camine hacia la cámara..."
                    className="w-full text-[15px] text-brand-black resize-none border border-neutral-200 rounded-md outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary p-4 bg-surface-card min-h-[100px]"
                  />
                  <div className="mt-5">
                    <label className="block text-[15px] font-medium text-brand-black/90 mb-2">Duración del video</label>
                    <div className="flex rounded-md border border-neutral-200 overflow-hidden">
                      {([['4', '4 seg'], ['6', '6 seg'], ['8', '8 seg']] as const).map(([val, label]) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setVideoDuration(val as '4' | '6' | '8')}
                          className={`flex-1 h-10 text-[15px] font-medium transition-colors ${
                            videoDuration === val
                              ? 'bg-neutral-100 text-neutral-900'
                              : 'bg-surface-card text-neutral-400 hover:bg-neutral-50'
                          } ${val !== '4' ? 'border-l border-neutral-200' : ''}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 px-1">
                    <Info className="w-3.5 h-3.5 text-info flex-shrink-0" />
                    <p className="text-[13px] text-neutral-500">Se consumirán {videoDuration === '4' ? 12 : videoDuration === '8' ? 24 : 18} imágenes.</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Loading animation for generating video */}
      {isGeneratingVideo && (
        <LoadingAnimation
          message="Generando video..."
          subMessage="Esto puede tardar hasta 2 minutos"
          durationMs={120000}
          completeNow={videoProgressComplete}
          onCompleted={() => {
            setIsGeneratingVideo(false);
          }}
        />
      )}
    </div>
  );
};

export default GeneratedImagesInline;
